import { useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { IoClose } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { ActionIcon, Button, Input, Modal, Select, Switch, Text, Textarea } from 'rizzui';
import { toast } from 'sonner';
import * as yup from 'yup';
import CustomSelect from '../../../components/forms/CustomSelect';
import RequiredInput from '../../../components/forms/InputHOC';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useHandleErrors from '../../../hooks/useHandleErrors';
import useProductTypes from '../../../hooks/api-calls/useProductTypes';
import useCorporations from '../../../hooks/api-calls/useCorporations';
import useContractStates from '../../../hooks/api-calls/contract/useContractStates';
import useContractProductsStates from '../../../hooks/api-calls/contract/useContractProductsStates';
import useContractInvoiceStates from '../../../hooks/api-calls/contract/useContractInvoicingStates';
import useContractIncidenceStates from '../../../hooks/api-calls/contract/useContractIncidenceStates';
import useContractCommissionStates from '../../../hooks/api-calls/contract/useContractCommissionStates';
import useExpirationManagementStates from '../../../hooks/api-calls/expirations/useExpirationManagementStates';
import useExpirationStates from '../../../hooks/api-calls/expirations/useExpirationStates';
import useOrderStatuses from '../../../hooks/api-calls/orders/useOrderStatuses';
import { menuRoutes } from '../../../router/menu';
import { DocumentTypeService } from '../../../services/document-type/documentTypeService';
import { EventsService } from '../../../services/events/eventsService';
import { UserService } from '../../../services/user/userService';
import { EventFieldsModel, EventRule } from '../../../type/entities/events-type';
import { buildBrandedEmailPreviewTemplate } from '../../../utils/emailTemplatePreview';

interface EventModalFormProps {
    data?: EventRule | null;
    initialEvent?: string | null;
    initialEntity?: string | null;
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
}

const EVENT_CONTRACT_CLIENT_UPDATE_CREDITS = 'contract.client.update.credits';
const EVENT_CONTRACT_DOCUMENT_UPLOAD = 'contract.document.upload';
const EVENT_ORDER_CHANGE_STATUS = 'order.change.status';
const EVENT_CONTRACT_COURSE_START_TODAY = 'contract.course.start.today';
const EVENT_CONTRACT_COURSE_END_TODAY = 'contract.course.end.today';
const EVENT_CONTRACT_SIGNATURE_RESEND = 'contract.signature.resend';
const EVENT_CONTRACT_REMITTANCE_CONFIRM = 'contract.remittance.confirm';
const EVENT_CONTRACT_IN_STATUS_WITHOUT_INCIDENCE_AND_PAID_INVOICE = 'contract.in.status.without.incidence.paid.invoice';
const EVENT_EXPIRATION_REMITTANCE_GENERATE = 'expiration.remittance.generate';
const EVENT_EXPIRATION_REMITTANCE_CONFIRM = 'expiration.remittance.confirm';
const EVENT_INVOICE_UNPAID_WEEKLY_REMINDER = 'invoice.unpaid.weekly.reminder';
const EVENT_INVOICE_UNPAID_MONTHLY_EXPIRATIONS_REMINDER = 'invoice.unpaid.monthly.expirations.reminder';
const CONTRACT_COURSE_DATE_EVENTS = [EVENT_CONTRACT_COURSE_START_TODAY, EVENT_CONTRACT_COURSE_END_TODAY];

export const ENTITY_OPTIONS = [
    { label: 'Contratos', value: 'contracts' },
    { label: 'Vencimientos', value: 'expirations' },
    { label: 'Pedidos', value: 'orders' },
];

export const CONTRACT_EVENT_OPTIONS = [
    { label: 'Firma de contrato', value: 'contract.sign' },
    { label: 'Cambio de estado de contrato', value: 'contract.change.status' },
    { label: 'Envío del contrato', value: 'contract.send' },
    { label: 'Registro de contrato', value: 'contract.register' },
    { label: 'Actualización de créditos de cliente', value: 'contract.client.update.credits' },
    { label: 'Subida de documento a contrato', value: EVENT_CONTRACT_DOCUMENT_UPLOAD },
    { label: 'Inicio de curso', value: EVENT_CONTRACT_COURSE_START_TODAY },
    { label: 'Fin de curso', value: EVENT_CONTRACT_COURSE_END_TODAY },
    { label: 'Reenvío de firma de contrato', value: EVENT_CONTRACT_SIGNATURE_RESEND },
    { label: 'Confirmación de remesa', value: EVENT_CONTRACT_REMITTANCE_CONFIRM },
    { label: 'Contrato finalizado, pagado y sin incidencias', value: EVENT_CONTRACT_IN_STATUS_WITHOUT_INCIDENCE_AND_PAID_INVOICE },
    { label: 'Recordatorio de factura impagada', value: EVENT_INVOICE_UNPAID_WEEKLY_REMINDER },
    { label: 'Recordatorio mensual de vencimientos próximo a cobro', value: EVENT_INVOICE_UNPAID_MONTHLY_EXPIRATIONS_REMINDER },
];

export const EXPIRATION_EVENT_OPTIONS = [
    { label: 'Cambio automático de gestión de cobro', value: 'expiration.management.change.status' },
    { label: 'Cambio de estado al generar remesa', value: EVENT_EXPIRATION_REMITTANCE_GENERATE },
    { label: 'Cambio de estado al confirmar remesa', value: EVENT_EXPIRATION_REMITTANCE_CONFIRM },
];

export const ORDER_EVENT_OPTIONS = [
    { label: 'Cambio de estado de pedido', value: EVENT_ORDER_CHANGE_STATUS },
];

export const INVOICE_EVENT_OPTIONS: { label: string; value: string }[] = [];

export const EVENT_OPTIONS_BY_ENTITY: Record<string, { label: string; value: string }[]> = {
    contracts: CONTRACT_EVENT_OPTIONS,
    expirations: EXPIRATION_EVENT_OPTIONS,
    orders: ORDER_EVENT_OPTIONS,
};

export const STATUS_TYPE_OPTIONS = [
    { label: 'Estado de contrato', value: 'contract' },
    { label: 'Estado de producto', value: 'contract_product' },
    { label: 'Estado de facturación', value: 'contract_invoicing' },
    { label: 'Estado de incidencia', value: 'contract_incidence' },
    { label: 'Estado de comisión', value: 'contract_commission' },
    { label: 'Estado de vencimiento', value: 'expiration' },
    { label: 'Estado de factura', value: 'invoice' },
    { label: 'Estado de gestión de cobro', value: 'expiration_management' },
];

const CONTRACT_STATUS_TYPE_OPTIONS = STATUS_TYPE_OPTIONS.filter((option) =>
    !['expiration_management', 'invoice'].includes(option.value)
);

export const DESTINATION_STATUS_MODE_OPTIONS = [
    { label: 'Estado específico', value: 'specific' },
    { label: 'Estado anterior (si existe)', value: 'previous' },
];

export const TEMPORAL_SCHEDULE_MODE_OPTIONS = [
    { label: 'Número de días personalizado', value: 'custom_days' },
    { label: 'Todos los días', value: 'daily' },
    { label: 'Cada semana', value: 'weekly' },
    { label: 'Cada mes', value: 'monthly' },
    { label: 'Cada año', value: 'yearly' },
];

const COURSE_START_TODAY_SCHEDULE_MODE_OPTIONS = TEMPORAL_SCHEDULE_MODE_OPTIONS.filter(
    (option) => option.value !== 'custom_days'
);

const WEEKDAY_OPTIONS = [
    { label: 'Lunes', value: 1 },
    { label: 'Martes', value: 2 },
    { label: 'Miércoles', value: 3 },
    { label: 'Jueves', value: 4 },
    { label: 'Viernes', value: 5 },
    { label: 'Sábado', value: 6 },
    { label: 'Domingo', value: 7 },
];

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, index) => ({
    label: String(index + 1),
    value: index + 1,
}));

const MONTH_OPTIONS = [
    { label: 'Enero', value: 1 },
    { label: 'Febrero', value: 2 },
    { label: 'Marzo', value: 3 },
    { label: 'Abril', value: 4 },
    { label: 'Mayo', value: 5 },
    { label: 'Junio', value: 6 },
    { label: 'Julio', value: 7 },
    { label: 'Agosto', value: 8 },
    { label: 'Septiembre', value: 9 },
    { label: 'Octubre', value: 10 },
    { label: 'Noviembre', value: 11 },
    { label: 'Diciembre', value: 12 },
];

interface EventFormData {
    id?: string;
    name?: string;
    description?: string;
    entity: string;
    event: string;
    contractProductTypeId: string | null;
    contractDocumentTypeIds: number[];
    contractEventContractStatusId: string | null;
    contractEventContractProductStatusId: string | null;
    contractEventContractInvoiceStatusId: string | null;
    contractEventContractIncidenceStatusId: string | null;
    contractEventContractCommissionStatusId: string | null;
    originStatusType: string | null;
    originStatusId: string | null;
    destinationStatusType: string | null;
    destinationStatusMode: string;
    destinationStatusId: string | null;
    scheduleMode: string | null;
    scheduleHour: string | null;
    scheduleWeekday: number | null;
    scheduleDay: number | null;
    scheduleMonth: number | null;
    scheduleCustomDays: number | null;
    sendSms: boolean;
    sendEmail: boolean;
    sendEmailConsultancyCopy: boolean;
    emailAttachmentDocumentTypeIds: number[];
    emailTemplateIds: string[];
    emailTemplateId: string | null;
}

interface EmailTemplateOption {
    label: string;
    value: string;
    name: string;
    templateContent: string;
    products: Array<{ id: string; name: string; productType?: { id: string; name: string } }>;
    organisingCorporations: Array<{ id: string; name: string }>;
}

const schema = yup.object({
    name: yup.string().nullable(),
    description: yup.string().nullable(),
    entity: yup.string().required('Campo obligatorio'),
    event: yup.string().required('Campo obligatorio'),
    contractProductTypeId: yup.string().nullable(),
    contractDocumentTypeIds: yup.array().of(yup.number().integer().positive()).required(),
    contractEventContractStatusId: yup.string().nullable(),
    contractEventContractProductStatusId: yup.string().nullable(),
    contractEventContractInvoiceStatusId: yup.string().nullable(),
    contractEventContractIncidenceStatusId: yup.string().nullable(),
    contractEventContractCommissionStatusId: yup.string().nullable(),
    originStatusType: yup.string().nullable().when('event', {
        is: 'contract.change.status',
        then: (value) => value.required('Campo obligatorio'),
        otherwise: (value) => value.nullable(),
    }),
    originStatusId: yup.string().nullable().when('event', {
        is: 'contract.change.status',
        then: (value) => value.required('Campo obligatorio'),
        otherwise: (value) => value.nullable(),
    }),
    destinationStatusType: yup.string().nullable().when('event', {
        is: 'contract.change.status',
        then: (value) => value.required('Campo obligatorio'),
        otherwise: (value) => value.nullable(),
    }),
    destinationStatusMode: yup.string().nullable().when('event', {
        is: 'contract.change.status',
        then: (value) => value.required('Campo obligatorio'),
        otherwise: (value) => value.nullable(),
    }),
    destinationStatusId: yup.string().nullable(),
    scheduleMode: yup.string().nullable(),
    scheduleHour: yup.string().nullable(),
    scheduleWeekday: yup.number().nullable(),
    scheduleDay: yup.number().nullable(),
    scheduleMonth: yup.number().nullable(),
    scheduleCustomDays: yup.number().nullable(),
    sendSms: yup.boolean().required(),
    sendEmail: yup.boolean().required(),
    sendEmailConsultancyCopy: yup.boolean().required(),
    emailAttachmentDocumentTypeIds: yup.array().of(yup.number().integer().positive()).required(),
    emailTemplateIds: yup.array().of(yup.string().required()).when('sendEmail', {
        is: true,
        then: (value) => value.min(1, 'Campo obligatorio'),
        otherwise: (value) => value.nullable(),
    }),
    emailTemplateId: yup.string().nullable(),
}).test(
    'require-at-least-one-status-for-sign-register',
    'Debe indicar al menos un estado destino (contrato, producto, facturación, incidencia o comisión)',
    function (values) {
        if (!values) {
            return true;
        }

        if (!['contract.sign', 'contract.register', EVENT_CONTRACT_CLIENT_UPDATE_CREDITS, EVENT_CONTRACT_DOCUMENT_UPLOAD, EVENT_CONTRACT_REMITTANCE_CONFIRM, EVENT_ORDER_CHANGE_STATUS, ...CONTRACT_COURSE_DATE_EVENTS].includes(values.event)) {
            return true;
        }

        const hasAtLeastOneStatus = Boolean(
            values.contractEventContractStatusId ||
            values.contractEventContractProductStatusId ||
            values.contractEventContractInvoiceStatusId ||
            values.contractEventContractIncidenceStatusId ||
            values.contractEventContractCommissionStatusId
        );

        if (!hasAtLeastOneStatus) {
            return this.createError({
                path: 'contractEventContractStatusId',
                message: 'Debe indicar al menos un estado destino (contrato, producto, facturación, incidencia o comisión)',
            });
        }

        return true;
    }
).test(
    'require-contract-document-types-for-upload-event',
    'Debe indicar uno o varios tipos de documento del contrato que disparan el evento',
    function (values) {
        if (!values || values.event !== EVENT_CONTRACT_DOCUMENT_UPLOAD) {
            return true;
        }

        if (!Array.isArray(values.contractDocumentTypeIds) || values.contractDocumentTypeIds.length === 0) {
            return this.createError({
                path: 'contractDocumentTypeIds',
                message: 'Debe indicar uno o varios tipos de documento del contrato que disparan el evento',
            });
        }

        return true;
    }
).test(
    'require-order-origin-status',
    'Debe indicar el estado de pedido que dispara el evento',
    function (values) {
        if (!values || values.event !== EVENT_ORDER_CHANGE_STATUS) {
            return true;
        }

        if (!values.originStatusId) {
            return this.createError({
                path: 'originStatusId',
                message: 'Debe indicar el estado de pedido que dispara el evento',
            });
        }

        return true;
    }
).test(
    'require-destination-status-id-when-specific',
    'Debe indicar el estado de destino',
    function (values) {
        if (!values || values.event !== 'contract.change.status') {
            return true;
        }

        if (values.destinationStatusMode === 'specific' && !values.destinationStatusId) {
            return this.createError({
                path: 'destinationStatusId',
                message: 'Debe indicar el estado de destino',
            });
        }

        return true;
    }
).test(
    'require-expiration-origin-destination',
    'Debe indicar estado de origen y destino para eventos de vencimientos',
    function (values) {
        if (!values || values.event !== 'expiration.management.change.status') {
            return true;
        }

        if (!values.originStatusId) {
            return this.createError({
                path: 'originStatusId',
                message: 'Debe indicar el estado de origen',
            });
        }

        if (!values.destinationStatusId) {
            return this.createError({
                path: 'destinationStatusId',
                message: 'Debe indicar el estado de destino',
            });
        }

        if (values.originStatusId === values.destinationStatusId) {
            return this.createError({
                path: 'destinationStatusId',
                message: 'El estado de destino no puede ser igual al de origen',
            });
        }

        return true;
    }
).test(
    'require-expiration-remittance-destination',
    'Debe indicar estado de vencimiento destino para eventos de remesas',
    function (values) {
        if (!values || ![EVENT_EXPIRATION_REMITTANCE_GENERATE, EVENT_EXPIRATION_REMITTANCE_CONFIRM].includes(values.event)) {
            return true;
        }

        if (values.entity !== 'expirations') {
            return this.createError({
                path: 'entity',
                message: 'Este evento solo aplica a la entidad Vencimientos',
            });
        }

        if (!values.destinationStatusId) {
            return this.createError({
                path: 'destinationStatusId',
                message: 'Debe indicar el estado de vencimiento destino',
            });
        }

        return true;
    }
).test(
    'require-course-start-today-origin-product-status',
    'Debe indicar el estado de producto previo requerido',
    function (values) {
        if (!values || !CONTRACT_COURSE_DATE_EVENTS.includes(values.event)) {
            return true;
        }

        if (!values.originStatusId) {
            return this.createError({
                path: 'originStatusId',
                message: 'Debe indicar el estado de producto previo requerido',
            });
        }

        return true;
    }
).test(
    'require-course-start-today-schedule-fields',
    'Debe indicar una frecuencia de comprobación válida',
    function (values) {
        if (!values || !CONTRACT_COURSE_DATE_EVENTS.includes(values.event)) {
            return true;
        }

        if (!values.scheduleMode) {
            return this.createError({
                path: 'scheduleMode',
                message: 'Debe indicar una frecuencia de comprobación',
            });
        }

        if (values.scheduleMode === 'custom_days') {
            return this.createError({
                path: 'scheduleMode',
                message: 'Para este evento no está permitido el modo de días personalizados',
            });
        }

        if (values.scheduleMode === 'daily' && !values.scheduleHour) {
            return this.createError({
                path: 'scheduleHour',
                message: 'Debe indicar una hora',
            });
        }

        if (values.scheduleMode === 'weekly' && !values.scheduleWeekday) {
            return this.createError({
                path: 'scheduleWeekday',
                message: 'Debe indicar el día de la semana',
            });
        }

        if ((values.scheduleMode === 'monthly' || values.scheduleMode === 'yearly') && !values.scheduleDay) {
            return this.createError({
                path: 'scheduleDay',
                message: 'Debe indicar el día del mes',
            });
        }

        if (values.scheduleMode === 'yearly' && !values.scheduleMonth) {
            return this.createError({
                path: 'scheduleMonth',
                message: 'Debe indicar el mes',
            });
        }

        return true;
    }
).test(
    'require-expiration-schedule-fields',
    'Debe indicar una programación temporal válida',
    function (values) {
        if (!values || values.event !== 'expiration.management.change.status') {
            return true;
        }

        if (!values.scheduleMode) {
            return this.createError({
                path: 'scheduleMode',
                message: 'Debe indicar una programación temporal',
            });
        }

        if (values.scheduleMode === 'custom_days' && (!values.scheduleCustomDays || values.scheduleCustomDays < 1)) {
            return this.createError({
                path: 'scheduleCustomDays',
                message: 'Debe indicar un número de días mayor que 0',
            });
        }

        if (values.scheduleMode === 'daily' && !values.scheduleHour) {
            return this.createError({
                path: 'scheduleHour',
                message: 'Debe indicar una hora',
            });
        }

        if (values.scheduleMode === 'weekly' && !values.scheduleWeekday) {
            return this.createError({
                path: 'scheduleWeekday',
                message: 'Debe indicar el día de la semana',
            });
        }

        if ((values.scheduleMode === 'monthly' || values.scheduleMode === 'yearly') && !values.scheduleDay) {
            return this.createError({
                path: 'scheduleDay',
                message: 'Debe indicar el día del mes',
            });
        }

        if (values.scheduleMode === 'yearly' && !values.scheduleMonth) {
            return this.createError({
                path: 'scheduleMonth',
                message: 'Debe indicar el mes',
            });
        }

        return true;
    }
).test(
    'require-invoice-unpaid-weekly-reminder-fields',
    'Debe indicar estado de facturación y programación temporal válida',
    function (values) {
        if (!values || values.event !== EVENT_INVOICE_UNPAID_WEEKLY_REMINDER) {
            return true;
        }

        if (values.entity !== 'contracts') {
            return this.createError({
                path: 'entity',
                message: 'Este evento solo aplica a la entidad Contratos',
            });
        }

        if (!values.originStatusId) {
            return this.createError({
                path: 'originStatusId',
                message: 'Debe indicar el estado de facturación a monitorizar',
            });
        }

        if (!values.scheduleMode) {
            return this.createError({
                path: 'scheduleMode',
                message: 'Debe indicar una programación temporal',
            });
        }

        if (values.scheduleMode === 'custom_days' && (!values.scheduleCustomDays || values.scheduleCustomDays < 1)) {
            return this.createError({
                path: 'scheduleCustomDays',
                message: 'Debe indicar un número de días mayor que 0',
            });
        }

        if (values.scheduleMode === 'daily' && !values.scheduleHour) {
            return this.createError({
                path: 'scheduleHour',
                message: 'Debe indicar una hora',
            });
        }

        if (values.scheduleMode === 'weekly' && !values.scheduleWeekday) {
            return this.createError({
                path: 'scheduleWeekday',
                message: 'Debe indicar el día de la semana',
            });
        }

        if ((values.scheduleMode === 'monthly' || values.scheduleMode === 'yearly') && !values.scheduleDay) {
            return this.createError({
                path: 'scheduleDay',
                message: 'Debe indicar el día del mes',
            });
        }

        if (values.scheduleMode === 'yearly' && !values.scheduleMonth) {
            return this.createError({
                path: 'scheduleMonth',
                message: 'Debe indicar el mes',
            });
        }

        if (!values.sendEmail) {
            return this.createError({
                path: 'sendEmail',
                message: 'Este evento requiere envío por email',
            });
        }

        return true;
    }
).test(
    'require-contract-in-status-without-incidence-and-paid-invoice-fields',
    'Debe indicar estado, días, estado de producto destino y documentación',
    function (values) {
        if (!values || values.event !== EVENT_CONTRACT_IN_STATUS_WITHOUT_INCIDENCE_AND_PAID_INVOICE) {
            return true;
        }

        if (values.entity !== 'contracts') {
            return this.createError({
                path: 'entity',
                message: 'Este evento solo aplica a la entidad Contratos',
            });
        }

        if (!values.originStatusId) {
            return this.createError({
                path: 'originStatusId',
                message: 'Debe indicar el estado de contrato a monitorizar',
            });
        }

        if (!values.contractEventContractProductStatusId) {
            return this.createError({
                path: 'contractEventContractProductStatusId',
                message: 'Debe indicar el estado de producto destino',
            });
        }

        if (!values.scheduleCustomDays || values.scheduleCustomDays < 1) {
            return this.createError({
                path: 'scheduleCustomDays',
                message: 'Debe indicar un número de días mayor que 0',
            });
        }

        if (values.scheduleMode && values.scheduleMode !== 'custom_days') {
            return this.createError({
                path: 'scheduleMode',
                message: 'Este evento solo permite número de días',
            });
        }

        if (!values.sendEmail) {
            return this.createError({
                path: 'sendEmail',
                message: 'Este evento requiere envío por email',
            });
        }

        if (!Array.isArray(values.emailAttachmentDocumentTypeIds) || values.emailAttachmentDocumentTypeIds.length === 0) {
            return this.createError({
                path: 'emailAttachmentDocumentTypeIds',
                message: 'Debe indicar uno o varios tipos de documento a adjuntar',
            });
        }

        return true;
    }
).test(
    'require-contract-signature-resend-fields',
    'Debe indicar minutos y activar al menos un canal para reenvío de firma',
    function (values) {
        if (!values || values.event !== EVENT_CONTRACT_SIGNATURE_RESEND) {
            return true;
        }

        if (values.entity !== 'contracts') {
            return this.createError({
                path: 'entity',
                message: 'Este evento solo aplica a la entidad Contratos',
            });
        }

        if (!values.scheduleCustomDays || values.scheduleCustomDays < 1) {
            return this.createError({
                path: 'scheduleCustomDays',
                message: 'Debe indicar minutos entre envíos (mayor que 0)',
            });
        }

        if (!values.sendSms && !values.sendEmail) {
            return this.createError({
                path: 'sendSms',
                message: 'Debe activar al menos un canal de envío (SMS o email)',
            });
        }

        return true;
    }
).test(
    'require-invoice-unpaid-monthly-expiration-reminder-fields',
    'Debe indicar un día del mes y una plantilla para el recordatorio mensual',
    function (values) {
        if (!values || values.event !== EVENT_INVOICE_UNPAID_MONTHLY_EXPIRATIONS_REMINDER) {
            return true;
        }

        if (values.entity !== 'contracts') {
            return this.createError({
                path: 'entity',
                message: 'Este evento solo aplica a la entidad Contratos',
            });
        }

        if (!values.scheduleDay || values.scheduleDay < 1 || values.scheduleDay > 31) {
            return this.createError({
                path: 'scheduleDay',
                message: 'Debe indicar el día del mes de envío',
            });
        }

        if (values.scheduleMode && values.scheduleMode !== 'monthly') {
            return this.createError({
                path: 'scheduleMode',
                message: 'Este evento solo permite programación mensual',
            });
        }

        if (!values.sendEmail) {
            return this.createError({
                path: 'sendEmail',
                message: 'Este evento requiere envío por email',
            });
        }

        return true;
    }
);

const EventModalForm: React.FC<EventModalFormProps> = ({
    data,
    initialEvent = null,
    initialEntity = null,
    isOpen,
    onClose,
    refetch
}) => {

    const eventsService = new EventsService();
    const navigate = useNavigate();
    const userService = useMemo(() => new UserService(), []);
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const mode = data?.id ? 'Editar' : 'Crear';
    const canCreateEvents = userCan('create_events', 'events');
    const canEditEvents = userCan('edit_events', 'events');
    const canAdminCompany = userCan('admin_company', 'companies');
    const canSubmitEventForm = mode === 'Editar'
        ? canEditEvents
        : canCreateEvents && canAdminCompany;
    const [emailTemplateOptions, setEmailTemplateOptions] = useState<EmailTemplateOption[]>([]);
    const [emailTemplateOrganisingCorporationFilterId, setEmailTemplateOrganisingCorporationFilterId] = useState<string | null>(null);
    const [contractDocumentTypeOptions, setContractDocumentTypeOptions] = useState<Array<{ label: string; value: number }>>([]);
    const [isLoadingEmailTemplates, setIsLoadingEmailTemplates] = useState(false);
    const [hasLoadedEmailTemplates, setHasLoadedEmailTemplates] = useState(false);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

    const { fetchProductTypes, getProductTypesList } = useProductTypes();
    const { fetchCorporations: fetchOrganisingCorporations, getCorporationsList: getOrganisingCorporationsList } = useCorporations({ corporation_function: 'organising' });
    const { fetchContractStates, getContractStatesList } = useContractStates({ entity: 'contract' });
    const { fetchContractProductsStates, getContractProductsStatesList } = useContractProductsStates({ entity: 'contract_product' });
    const { fetchContractInvoiceStates, getContractInvoiceStatesList } = useContractInvoiceStates({ entity: 'contract_invoicing' });
    const { fetchContractIncidenceStates, getContractIncidenceStatesList } = useContractIncidenceStates({ entity: 'contract_incidence' });
    const { fetchContractCommissionStates, getContractCommissionStatesList } = useContractCommissionStates({ entity: 'contract_commission' });
    const { fetchExpirationStates, getExpirationStatesList } = useExpirationStates();
    const { fetchExpirationManagementStates, getExpirationManagementStatesList } = useExpirationManagementStates();
    const { fetchOrderStatusesList, getOrderStatusesList } = useOrderStatuses();

    const mapSelectOption = (value: string | number | null | undefined, options: any[]) =>
        options.find((option) => option.value === value) || null;

    const mapMultiSelectOptions = (values: Array<string | number> | null | undefined, options: any[]) => {
        if (!Array.isArray(values) || values.length === 0) {
            return [];
        }

        const ids = new Set(values.map((value) => String(value)));
        return options.filter((option) => ids.has(String(option.value)));
    };

    const toSelectOptions = (options: any[]) =>
        options.map((option: any) => ({
            label: option.label,
            value: option.value,
        }));

    const getEventOptionsByEntity = (entity: string) => EVENT_OPTIONS_BY_ENTITY[entity] || [];

    const create = async (values: EventFormData) => {
        const isContractChangeStatusEvent = values.event === 'contract.change.status';
        const isContractDocumentUploadEvent = values.event === EVENT_CONTRACT_DOCUMENT_UPLOAD;
        const isContractInStatusWithoutIncidenceAndPaidInvoiceEvent =
            values.event === EVENT_CONTRACT_IN_STATUS_WITHOUT_INCIDENCE_AND_PAID_INVOICE;
        const isOrderChangeStatusEvent = values.event === EVENT_ORDER_CHANGE_STATUS;
        const isContractCourseDateTodayEvent = CONTRACT_COURSE_DATE_EVENTS.includes(values.event);
        const isContractSignatureResendEvent = values.event === EVENT_CONTRACT_SIGNATURE_RESEND;
        const isExpirationManagementEvent = values.event === 'expiration.management.change.status';
        const isExpirationRemittanceEvent = [EVENT_EXPIRATION_REMITTANCE_GENERATE, EVENT_EXPIRATION_REMITTANCE_CONFIRM].includes(values.event);
        const isInvoiceUnpaidReminderEvent = values.event === EVENT_INVOICE_UNPAID_WEEKLY_REMINDER;
        const isInvoiceUnpaidMonthlyExpirationsReminderEvent = values.event === EVENT_INVOICE_UNPAID_MONTHLY_EXPIRATIONS_REMINDER;
        const isInvoiceAutomatedReminderEvent = isInvoiceUnpaidReminderEvent || isInvoiceUnpaidMonthlyExpirationsReminderEvent;
        const shouldSendContractDestinationStatuses = (
            (values.entity === 'contracts' && !isInvoiceAutomatedReminderEvent && !isContractInStatusWithoutIncidenceAndPaidInvoiceEvent)
            || (values.entity === 'orders' && isOrderChangeStatusEvent)
        );
        const eventEntityUseType = getEmailTemplateUseType(values.entity);
        const canUseEmailTemplates = Boolean(eventEntityUseType);
        const sendEmail = canUseEmailTemplates
            ? ((isInvoiceAutomatedReminderEvent || isContractInStatusWithoutIncidenceAndPaidInvoiceEvent) ? true : values.sendEmail)
            : false;

        const payload: EventFieldsModel = {
            name: values.name || null,
            description: values.description || null,
            entity: values.entity,
            event: values.event,
            contractProductTypeId: ['contracts', 'orders'].includes(values.entity) ? values.contractProductTypeId : null,
            contractDocumentTypeIds: isContractDocumentUploadEvent ? values.contractDocumentTypeIds : [],
            contractEventContractStatusId: shouldSendContractDestinationStatuses ? values.contractEventContractStatusId : null,
            contractEventContractProductStatusId: (shouldSendContractDestinationStatuses || isContractInStatusWithoutIncidenceAndPaidInvoiceEvent)
                ? values.contractEventContractProductStatusId
                : null,
            contractEventContractInvoiceStatusId: shouldSendContractDestinationStatuses ? values.contractEventContractInvoiceStatusId : null,
            contractEventContractIncidenceStatusId: shouldSendContractDestinationStatuses ? values.contractEventContractIncidenceStatusId : null,
            contractEventContractCommissionStatusId: shouldSendContractDestinationStatuses ? values.contractEventContractCommissionStatusId : null,
            originStatusType: isOrderChangeStatusEvent
                ? 'order'
                : isContractInStatusWithoutIncidenceAndPaidInvoiceEvent
                    ? 'contract'
                : isContractCourseDateTodayEvent
                ? 'contract_product'
                : isExpirationManagementEvent
                ? 'expiration_management'
                : isExpirationRemittanceEvent
                    ? null
                : isInvoiceUnpaidReminderEvent
                    ? 'contract_invoicing'
                    : isInvoiceUnpaidMonthlyExpirationsReminderEvent
                        ? null
                    : values.originStatusType,
            originStatusId: (
                isContractChangeStatusEvent
                || isOrderChangeStatusEvent
                || isExpirationManagementEvent
                || isInvoiceUnpaidReminderEvent
                || isContractInStatusWithoutIncidenceAndPaidInvoiceEvent
                || isContractCourseDateTodayEvent
            ) ? values.originStatusId : null,
            destinationStatusType: isExpirationManagementEvent
                ? 'expiration_management'
                : isExpirationRemittanceEvent
                    ? 'expiration'
                : isInvoiceAutomatedReminderEvent
                    ? null
                : values.destinationStatusType,
            destinationStatusMode: isContractChangeStatusEvent ? values.destinationStatusMode : 'specific',
            destinationStatusId: isContractChangeStatusEvent && values.destinationStatusMode === 'specific'
                ? values.destinationStatusId
                : isExpirationManagementEvent
                    ? values.destinationStatusId
                    : isExpirationRemittanceEvent
                        ? values.destinationStatusId
                    : null,
            scheduleMode: (isExpirationManagementEvent || isInvoiceAutomatedReminderEvent || isContractCourseDateTodayEvent || isContractInStatusWithoutIncidenceAndPaidInvoiceEvent || isContractSignatureResendEvent)
                ? (isInvoiceUnpaidMonthlyExpirationsReminderEvent ? 'monthly' : values.scheduleMode)
                : null,
            scheduleHour: (isExpirationManagementEvent || isInvoiceUnpaidReminderEvent || isContractCourseDateTodayEvent) ? values.scheduleHour : null,
            scheduleWeekday: (isExpirationManagementEvent || isInvoiceUnpaidReminderEvent || isContractCourseDateTodayEvent) ? values.scheduleWeekday : null,
            scheduleDay: (isExpirationManagementEvent || isInvoiceAutomatedReminderEvent || isContractCourseDateTodayEvent) ? values.scheduleDay : null,
            scheduleMonth: (isExpirationManagementEvent || isInvoiceUnpaidReminderEvent || isContractCourseDateTodayEvent) ? values.scheduleMonth : null,
            scheduleCustomDays: (isExpirationManagementEvent || isInvoiceUnpaidReminderEvent || isContractInStatusWithoutIncidenceAndPaidInvoiceEvent || isContractSignatureResendEvent) ? values.scheduleCustomDays : null,
            sendSms: [ 'contract.register', EVENT_CONTRACT_SIGNATURE_RESEND ].includes(values.event) ? values.sendSms : false,
            sendEmail,
            sendEmailConsultancyCopy: canUseEmailTemplates && sendEmail ? values.sendEmailConsultancyCopy : false,
            emailAttachmentDocumentTypeIds: canUseEmailTemplates && sendEmail ? values.emailAttachmentDocumentTypeIds : [],
            emailTemplateIds: canUseEmailTemplates && sendEmail ? values.emailTemplateIds : [],
            emailTemplateId: canUseEmailTemplates && sendEmail ? (values.emailTemplateIds[0] || null) : null,
        };

        try {
            const response = await (await eventsService.createEvent(payload)).getResponseData();
            if (response.success) {
                toast.success('Evento creado correctamente');
                refetch();
                handleClose();
                return;
            }
            handleErrors(response);
        } catch (error: any) {
            handleErrors(error);
        }
    };

    const edit = async (values: EventFormData) => {
        const isContractChangeStatusEvent = values.event === 'contract.change.status';
        const isContractDocumentUploadEvent = values.event === EVENT_CONTRACT_DOCUMENT_UPLOAD;
        const isContractInStatusWithoutIncidenceAndPaidInvoiceEvent =
            values.event === EVENT_CONTRACT_IN_STATUS_WITHOUT_INCIDENCE_AND_PAID_INVOICE;
        const isOrderChangeStatusEvent = values.event === EVENT_ORDER_CHANGE_STATUS;
        const isContractCourseDateTodayEvent = CONTRACT_COURSE_DATE_EVENTS.includes(values.event);
        const isContractSignatureResendEvent = values.event === EVENT_CONTRACT_SIGNATURE_RESEND;
        const isExpirationManagementEvent = values.event === 'expiration.management.change.status';
        const isExpirationRemittanceEvent = [EVENT_EXPIRATION_REMITTANCE_GENERATE, EVENT_EXPIRATION_REMITTANCE_CONFIRM].includes(values.event);
        const isInvoiceUnpaidReminderEvent = values.event === EVENT_INVOICE_UNPAID_WEEKLY_REMINDER;
        const isInvoiceUnpaidMonthlyExpirationsReminderEvent = values.event === EVENT_INVOICE_UNPAID_MONTHLY_EXPIRATIONS_REMINDER;
        const isInvoiceAutomatedReminderEvent = isInvoiceUnpaidReminderEvent || isInvoiceUnpaidMonthlyExpirationsReminderEvent;
        const shouldSendContractDestinationStatuses = (
            (values.entity === 'contracts' && !isInvoiceAutomatedReminderEvent && !isContractInStatusWithoutIncidenceAndPaidInvoiceEvent)
            || (values.entity === 'orders' && isOrderChangeStatusEvent)
        );
        const eventEntityUseType = getEmailTemplateUseType(values.entity);
        const canUseEmailTemplates = Boolean(eventEntityUseType);
        const sendEmail = canUseEmailTemplates
            ? ((isInvoiceAutomatedReminderEvent || isContractInStatusWithoutIncidenceAndPaidInvoiceEvent) ? true : values.sendEmail)
            : false;

        const payload: EventFieldsModel = {
            id: data?.id,
            name: values.name || null,
            description: values.description || null,
            entity: values.entity,
            event: values.event,
            contractProductTypeId: ['contracts', 'orders'].includes(values.entity) ? values.contractProductTypeId : null,
            contractDocumentTypeIds: isContractDocumentUploadEvent ? values.contractDocumentTypeIds : [],
            contractEventContractStatusId: shouldSendContractDestinationStatuses ? values.contractEventContractStatusId : null,
            contractEventContractProductStatusId: (shouldSendContractDestinationStatuses || isContractInStatusWithoutIncidenceAndPaidInvoiceEvent)
                ? values.contractEventContractProductStatusId
                : null,
            contractEventContractInvoiceStatusId: shouldSendContractDestinationStatuses ? values.contractEventContractInvoiceStatusId : null,
            contractEventContractIncidenceStatusId: shouldSendContractDestinationStatuses ? values.contractEventContractIncidenceStatusId : null,
            contractEventContractCommissionStatusId: shouldSendContractDestinationStatuses ? values.contractEventContractCommissionStatusId : null,
            originStatusType: isOrderChangeStatusEvent
                ? 'order'
                : isContractInStatusWithoutIncidenceAndPaidInvoiceEvent
                    ? 'contract'
                : isContractCourseDateTodayEvent
                ? 'contract_product'
                : isExpirationManagementEvent
                ? 'expiration_management'
                : isExpirationRemittanceEvent
                    ? null
                : isInvoiceUnpaidReminderEvent
                    ? 'contract_invoicing'
                    : isInvoiceUnpaidMonthlyExpirationsReminderEvent
                        ? null
                    : values.originStatusType,
            originStatusId: (
                isContractChangeStatusEvent
                || isOrderChangeStatusEvent
                || isExpirationManagementEvent
                || isInvoiceUnpaidReminderEvent
                || isContractInStatusWithoutIncidenceAndPaidInvoiceEvent
                || isContractCourseDateTodayEvent
            ) ? values.originStatusId : null,
            destinationStatusType: isExpirationManagementEvent
                ? 'expiration_management'
                : isExpirationRemittanceEvent
                    ? 'expiration'
                : isInvoiceAutomatedReminderEvent
                    ? null
                : values.destinationStatusType,
            destinationStatusMode: isContractChangeStatusEvent ? values.destinationStatusMode : 'specific',
            destinationStatusId: isContractChangeStatusEvent && values.destinationStatusMode === 'specific'
                ? values.destinationStatusId
                : isExpirationManagementEvent
                    ? values.destinationStatusId
                    : isExpirationRemittanceEvent
                        ? values.destinationStatusId
                    : null,
            scheduleMode: (isExpirationManagementEvent || isInvoiceAutomatedReminderEvent || isContractCourseDateTodayEvent || isContractInStatusWithoutIncidenceAndPaidInvoiceEvent || isContractSignatureResendEvent)
                ? (isInvoiceUnpaidMonthlyExpirationsReminderEvent ? 'monthly' : values.scheduleMode)
                : null,
            scheduleHour: (isExpirationManagementEvent || isInvoiceUnpaidReminderEvent || isContractCourseDateTodayEvent) ? values.scheduleHour : null,
            scheduleWeekday: (isExpirationManagementEvent || isInvoiceUnpaidReminderEvent || isContractCourseDateTodayEvent) ? values.scheduleWeekday : null,
            scheduleDay: (isExpirationManagementEvent || isInvoiceAutomatedReminderEvent || isContractCourseDateTodayEvent) ? values.scheduleDay : null,
            scheduleMonth: (isExpirationManagementEvent || isInvoiceUnpaidReminderEvent || isContractCourseDateTodayEvent) ? values.scheduleMonth : null,
            scheduleCustomDays: (isExpirationManagementEvent || isInvoiceUnpaidReminderEvent || isContractInStatusWithoutIncidenceAndPaidInvoiceEvent || isContractSignatureResendEvent) ? values.scheduleCustomDays : null,
            sendSms: [ 'contract.register', EVENT_CONTRACT_SIGNATURE_RESEND ].includes(values.event) ? values.sendSms : false,
            sendEmail,
            sendEmailConsultancyCopy: canUseEmailTemplates && sendEmail ? values.sendEmailConsultancyCopy : false,
            emailAttachmentDocumentTypeIds: canUseEmailTemplates && sendEmail ? values.emailAttachmentDocumentTypeIds : [],
            emailTemplateIds: canUseEmailTemplates && sendEmail ? values.emailTemplateIds : [],
            emailTemplateId: canUseEmailTemplates && sendEmail ? (values.emailTemplateIds[0] || null) : null,
        };

        try {
            const response = await (await eventsService.editEvent(payload)).getResponseData();
            if (response.success) {
                toast.success('Evento actualizado correctamente');
                refetch();
                handleClose();
                return;
            }
            handleErrors(response);
        } catch (error: any) {
            handleErrors(error);
        }
    };

    const formik = useFormik<EventFormData>({
        initialValues: {
            id: '',
            name: '',
            description: '',
            entity: 'contracts',
            event: '',
            contractProductTypeId: null,
            contractDocumentTypeIds: [],
            contractEventContractStatusId: null,
            contractEventContractProductStatusId: null,
            contractEventContractInvoiceStatusId: null,
            contractEventContractIncidenceStatusId: null,
            contractEventContractCommissionStatusId: null,
            originStatusType: null,
            originStatusId: null,
            destinationStatusType: null,
            destinationStatusMode: 'specific',
            destinationStatusId: null,
            scheduleMode: null,
            scheduleHour: null,
            scheduleWeekday: null,
            scheduleDay: null,
            scheduleMonth: null,
            scheduleCustomDays: null,
            sendSms: false,
            sendEmail: false,
            sendEmailConsultancyCopy: false,
            emailAttachmentDocumentTypeIds: [],
            emailTemplateIds: [],
            emailTemplateId: null,
        },
        validationSchema: schema,
        validateOnBlur: false,
        onSubmit: async (values) => {
            if (!canSubmitEventForm) {
                toast.error('No tienes permisos para guardar este evento');
                return;
            }

            if (mode === 'Editar') {
                await edit(values);
            } else {
                await create(values);
            }
        },
    });

    function handleClose() {
        formik.resetForm();
        onClose();
    }

    const filterStatusOptionsByProductType = (options: any[]) => {
        if (!formik.values.contractProductTypeId) {
            return options;
        }

        return options.filter((option: any) =>
            !Array.isArray(option.productTypes) ||
            option.productTypes.length === 0 ||
            option.productTypes.includes(formik.values.contractProductTypeId)
        );
    };

    const contractStateOptions = useMemo(
        () => filterStatusOptionsByProductType(getContractStatesList()),
        [getContractStatesList, formik.values.contractProductTypeId]
    );

    const contractProductStateOptions = useMemo(
        () => filterStatusOptionsByProductType(getContractProductsStatesList()),
        [getContractProductsStatesList, formik.values.contractProductTypeId]
    );

    const contractInvoiceStateOptions = useMemo(
        () => filterStatusOptionsByProductType(getContractInvoiceStatesList()),
        [getContractInvoiceStatesList, formik.values.contractProductTypeId]
    );

    const contractIncidenceStateOptions = useMemo(
        () => filterStatusOptionsByProductType(getContractIncidenceStatesList()),
        [getContractIncidenceStatesList, formik.values.contractProductTypeId]
    );

    const contractCommissionStateOptions = useMemo(
        () => filterStatusOptionsByProductType(getContractCommissionStatesList()),
        [getContractCommissionStatesList, formik.values.contractProductTypeId]
    );

    const stateOptionsByType: Record<string, any[]> = {
        contract: contractStateOptions,
        contract_product: contractProductStateOptions,
        contract_invoicing: contractInvoiceStateOptions,
        contract_incidence: contractIncidenceStateOptions,
        contract_commission: contractCommissionStateOptions,
        expiration: getExpirationStatesList(),
        expiration_management: getExpirationManagementStatesList(),
    };

    const getEmailTemplateUseType = (entity: string): string | null => {
        const useTypeByEntity: Record<string, string> = {
            contracts: 'contract',
            expirations: 'expiration',
        };

        return useTypeByEntity[entity] || null;
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        // Refresca catálogos al abrir para evitar selects vacíos/stale hasta interacción del usuario.
        fetchProductTypes();
        fetchContractStates();
        fetchContractProductsStates();
        fetchContractInvoiceStates();
        fetchContractIncidenceStates();
        fetchContractCommissionStates();
        fetchExpirationStates();
        fetchExpirationManagementStates();
        fetchOrderStatusesList();
        fetchOrganisingCorporations({ corporation_function: 'organising' });
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const fetchContractDocumentTypes = async () => {
            try {
                const response = await (await new DocumentTypeService().listDocumentTypes({
                    filter_filters: { entity: 'contract' },
                    limit: 999999,
                })).getResponseData();

                const mappedOptions = (response?.data?.data || [])
                    .filter((item: any) => item?.id && item?.name)
                    .map((item: any) => ({
                        value: Number(item.id),
                        label: item.name,
                    }));

                setContractDocumentTypeOptions(mappedOptions);
            } catch (error) {
                setContractDocumentTypeOptions([]);
            }
        };

        fetchContractDocumentTypes();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (data?.id) {
            formik.setValues({
                id: data.id,
                name: data.name || '',
                description: data.description || '',
                entity: data.entity,
                event: data.event,
                contractProductTypeId: data.contractProductType?.id || null,
                contractDocumentTypeIds: Array.isArray(data.contractDocumentTypes) && data.contractDocumentTypes.length > 0
                    ? data.contractDocumentTypes.map((item) => Number(item.id))
                    : (data.contractDocumentType?.id ? [Number(data.contractDocumentType.id)] : []),
                contractEventContractStatusId: data.eventContractStatus?.id || null,
                contractEventContractProductStatusId: data.eventContractProductStatus?.id || null,
                contractEventContractInvoiceStatusId: data.eventContractInvoiceStatus?.id || null,
                contractEventContractIncidenceStatusId: data.eventContractIncidenceStatus?.id || null,
                contractEventContractCommissionStatusId: data.eventContractCommissionStatus?.id || null,
                originStatusType: data.originStatusType || null,
                originStatusId: data.originStatus?.id || null,
                destinationStatusType: data.destinationStatusType || null,
                destinationStatusMode: data.destinationStatusMode || 'specific',
                destinationStatusId: data.destinationStatus?.id || null,
                scheduleMode: data.scheduleMode || null,
                scheduleHour: data.scheduleHour || null,
                scheduleWeekday: data.scheduleWeekday || null,
                scheduleDay: data.scheduleDay || null,
                scheduleMonth: data.scheduleMonth || null,
                scheduleCustomDays: data.scheduleCustomDays || null,
                sendSms: data.sendSms || false,
                sendEmail: data.sendEmail || false,
                sendEmailConsultancyCopy: data.sendEmailConsultancyCopy || false,
                emailAttachmentDocumentTypeIds: (data.emailAttachmentDocumentTypes || []).map((item) => Number(item.id)),
                emailTemplateIds: Array.isArray(data.emailTemplates) && data.emailTemplates.length > 0
                    ? data.emailTemplates.map((item) => item.id)
                    : (data.emailTemplate?.id ? [data.emailTemplate.id] : []),
                emailTemplateId: Array.isArray(data.emailTemplates) && data.emailTemplates.length > 0
                    ? data.emailTemplates[0].id
                    : (data.emailTemplate?.id || null),
            });
            return;
        }

        formik.setValues({
            id: '',
            name: '',
            description: '',
            entity: initialEntity || 'contracts',
            event: initialEvent || '',
            contractProductTypeId: null,
            contractDocumentTypeIds: [],
            contractEventContractStatusId: null,
            contractEventContractProductStatusId: null,
            contractEventContractInvoiceStatusId: null,
            contractEventContractIncidenceStatusId: null,
            contractEventContractCommissionStatusId: null,
            originStatusType: null,
            originStatusId: null,
            destinationStatusType: null,
            destinationStatusMode: 'specific',
            destinationStatusId: null,
            scheduleMode: null,
            scheduleHour: null,
            scheduleWeekday: null,
            scheduleDay: null,
            scheduleMonth: null,
            scheduleCustomDays: null,
            sendSms: false,
            sendEmail: false,
            sendEmailConsultancyCopy: false,
            emailAttachmentDocumentTypeIds: [],
            emailTemplateIds: [],
            emailTemplateId: null,
        });
    }, [data, initialEntity, initialEvent, isOpen]);

    useEffect(() => {
        if (![
            'contract.change.status',
            'expiration.management.change.status',
            EVENT_EXPIRATION_REMITTANCE_GENERATE,
            EVENT_EXPIRATION_REMITTANCE_CONFIRM,
            EVENT_INVOICE_UNPAID_WEEKLY_REMINDER,
            EVENT_INVOICE_UNPAID_MONTHLY_EXPIRATIONS_REMINDER,
            EVENT_CONTRACT_IN_STATUS_WITHOUT_INCIDENCE_AND_PAID_INVOICE,
            EVENT_ORDER_CHANGE_STATUS,
            ...CONTRACT_COURSE_DATE_EVENTS
        ].includes(formik.values.event)) {
            formik.setFieldValue('originStatusType', null);
            formik.setFieldValue('originStatusId', null);
            formik.setFieldValue('destinationStatusType', null);
            formik.setFieldValue('destinationStatusMode', 'specific');
            formik.setFieldValue('destinationStatusId', null);
        }
    }, [formik.values.event]);

    useEffect(() => {
        if (formik.values.event !== EVENT_ORDER_CHANGE_STATUS) {
            return;
        }

        formik.setFieldValue('originStatusType', 'order');
        formik.setFieldValue('destinationStatusType', null);
        formik.setFieldValue('destinationStatusMode', 'specific');
        formik.setFieldValue('destinationStatusId', null);
    }, [formik.values.event]);

    useEffect(() => {
        const validEvents = getEventOptionsByEntity(formik.values.entity).map((item) => item.value);
        if (!validEvents.includes(formik.values.event)) {
            formik.setFieldValue('event', '');
        }
    }, [formik.values.entity]);

    useEffect(() => {
        if (getEmailTemplateUseType(formik.values.entity)) {
            return;
        }

        formik.setFieldValue('sendEmail', false);
        formik.setFieldValue('emailTemplateIds', []);
        formik.setFieldValue('emailTemplateId', null);
    }, [formik.values.entity]);

    useEffect(() => {
        if (formik.values.destinationStatusMode === 'previous') {
            formik.setFieldValue('destinationStatusId', null);
        }
    }, [formik.values.destinationStatusMode]);

    useEffect(() => {
        if (formik.values.event === 'contract.change.status') {
            formik.setFieldValue('contractEventContractStatusId', null);
            formik.setFieldValue('contractEventContractProductStatusId', null);
            formik.setFieldValue('contractEventContractInvoiceStatusId', null);
            formik.setFieldValue('contractEventContractIncidenceStatusId', null);
            formik.setFieldValue('contractEventContractCommissionStatusId', null);
            return;
        }
    }, [formik.values.event]);

    useEffect(() => {
        if (formik.values.event === EVENT_CONTRACT_DOCUMENT_UPLOAD) {
            return;
        }

        formik.setFieldValue('contractDocumentTypeIds', []);
    }, [formik.values.event]);

    useEffect(() => {
        const isExpirationManagementEvent = formik.values.event === 'expiration.management.change.status';
        const isExpirationRemittanceEvent = [EVENT_EXPIRATION_REMITTANCE_GENERATE, EVENT_EXPIRATION_REMITTANCE_CONFIRM].includes(formik.values.event);
        const isInvoiceUnpaidReminderEvent = formik.values.event === EVENT_INVOICE_UNPAID_WEEKLY_REMINDER;
        const isInvoiceUnpaidMonthlyExpirationsReminderEvent = formik.values.event === EVENT_INVOICE_UNPAID_MONTHLY_EXPIRATIONS_REMINDER;
        const isContractInStatusWithoutIncidenceAndPaidInvoiceEvent =
            formik.values.event === EVENT_CONTRACT_IN_STATUS_WITHOUT_INCIDENCE_AND_PAID_INVOICE;
        const isContractCourseDateTodayEvent = CONTRACT_COURSE_DATE_EVENTS.includes(formik.values.event);
        const isContractSignatureResendEvent = formik.values.event === EVENT_CONTRACT_SIGNATURE_RESEND;
        const isAnyInvoiceReminderEvent = isInvoiceUnpaidReminderEvent || isInvoiceUnpaidMonthlyExpirationsReminderEvent;

        if (
            !isExpirationManagementEvent
            && !isExpirationRemittanceEvent
            && !isAnyInvoiceReminderEvent
            && !isContractCourseDateTodayEvent
            && !isContractInStatusWithoutIncidenceAndPaidInvoiceEvent
            && !isContractSignatureResendEvent
        ) {
            formik.setFieldValue('scheduleMode', null);
            formik.setFieldValue('scheduleHour', null);
            formik.setFieldValue('scheduleWeekday', null);
            formik.setFieldValue('scheduleDay', null);
            formik.setFieldValue('scheduleMonth', null);
            formik.setFieldValue('scheduleCustomDays', null);
            return;
        }

        if (isContractSignatureResendEvent) {
            formik.setFieldValue('originStatusType', null);
            formik.setFieldValue('originStatusId', null);
            formik.setFieldValue('destinationStatusType', null);
            formik.setFieldValue('destinationStatusMode', 'specific');
            formik.setFieldValue('destinationStatusId', null);
            formik.setFieldValue('scheduleMode', 'custom_days');
            formik.setFieldValue('scheduleHour', null);
            formik.setFieldValue('scheduleWeekday', null);
            formik.setFieldValue('scheduleDay', null);
            formik.setFieldValue('scheduleMonth', null);
            formik.setFieldValue('contractEventContractStatusId', null);
            formik.setFieldValue('contractEventContractProductStatusId', null);
            formik.setFieldValue('contractEventContractInvoiceStatusId', null);
            formik.setFieldValue('contractEventContractIncidenceStatusId', null);
            formik.setFieldValue('contractEventContractCommissionStatusId', null);
            return;
        }

        if (isExpirationManagementEvent) {
            formik.setFieldValue('originStatusType', 'expiration_management');
            formik.setFieldValue('destinationStatusType', 'expiration_management');
            formik.setFieldValue('destinationStatusMode', 'specific');
            formik.setFieldValue('contractProductTypeId', null);
            formik.setFieldValue('contractEventContractStatusId', null);
            formik.setFieldValue('contractEventContractProductStatusId', null);
            formik.setFieldValue('contractEventContractInvoiceStatusId', null);
            formik.setFieldValue('contractEventContractIncidenceStatusId', null);
            formik.setFieldValue('contractEventContractCommissionStatusId', null);
            formik.setFieldValue('sendSms', false);
            return;
        }

        if (isExpirationRemittanceEvent) {
            formik.setFieldValue('originStatusType', null);
            formik.setFieldValue('originStatusId', null);
            formik.setFieldValue('destinationStatusType', 'expiration');
            formik.setFieldValue('destinationStatusMode', 'specific');
            formik.setFieldValue('scheduleMode', null);
            formik.setFieldValue('scheduleHour', null);
            formik.setFieldValue('scheduleWeekday', null);
            formik.setFieldValue('scheduleDay', null);
            formik.setFieldValue('scheduleMonth', null);
            formik.setFieldValue('scheduleCustomDays', null);
            formik.setFieldValue('contractProductTypeId', null);
            formik.setFieldValue('contractEventContractStatusId', null);
            formik.setFieldValue('contractEventContractProductStatusId', null);
            formik.setFieldValue('contractEventContractInvoiceStatusId', null);
            formik.setFieldValue('contractEventContractIncidenceStatusId', null);
            formik.setFieldValue('contractEventContractCommissionStatusId', null);
            formik.setFieldValue('sendSms', false);
            return;
        }

        if (isContractCourseDateTodayEvent) {
            if (formik.values.originStatusType !== 'contract_product') {
                formik.setFieldValue('originStatusId', null);
            }

            formik.setFieldValue('originStatusType', 'contract_product');
            formik.setFieldValue('destinationStatusType', null);
            formik.setFieldValue('destinationStatusMode', 'specific');
            formik.setFieldValue('destinationStatusId', null);
            formik.setFieldValue('scheduleCustomDays', null);
            formik.setFieldValue('sendSms', false);

            if (!formik.values.scheduleMode || formik.values.scheduleMode === 'custom_days') {
                formik.setFieldValue('scheduleMode', 'daily');
            }

            return;
        }

        if (isContractInStatusWithoutIncidenceAndPaidInvoiceEvent) {
            if (formik.values.originStatusType !== 'contract') {
                formik.setFieldValue('originStatusId', null);
            }

            formik.setFieldValue('originStatusType', 'contract');
            formik.setFieldValue('destinationStatusType', null);
            formik.setFieldValue('destinationStatusMode', 'specific');
            formik.setFieldValue('destinationStatusId', null);
            formik.setFieldValue('scheduleMode', 'custom_days');
            formik.setFieldValue('scheduleHour', null);
            formik.setFieldValue('scheduleWeekday', null);
            formik.setFieldValue('scheduleDay', null);
            formik.setFieldValue('scheduleMonth', null);
            formik.setFieldValue('contractEventContractStatusId', null);
            formik.setFieldValue('contractEventContractInvoiceStatusId', null);
            formik.setFieldValue('contractEventContractIncidenceStatusId', null);
            formik.setFieldValue('contractEventContractCommissionStatusId', null);
            formik.setFieldValue('sendSms', false);
            formik.setFieldValue('sendEmail', true);
            return;
        }

        if (isInvoiceUnpaidMonthlyExpirationsReminderEvent) {
            formik.setFieldValue('originStatusType', null);
            formik.setFieldValue('originStatusId', null);
            formik.setFieldValue('destinationStatusType', null);
            formik.setFieldValue('destinationStatusMode', 'specific');
            formik.setFieldValue('destinationStatusId', null);
            formik.setFieldValue('scheduleMode', 'monthly');
            formik.setFieldValue('scheduleHour', null);
            formik.setFieldValue('scheduleWeekday', null);
            formik.setFieldValue('scheduleMonth', null);
            formik.setFieldValue('scheduleCustomDays', null);
            formik.setFieldValue('contractEventContractStatusId', null);
            formik.setFieldValue('contractEventContractProductStatusId', null);
            formik.setFieldValue('contractEventContractInvoiceStatusId', null);
            formik.setFieldValue('contractEventContractIncidenceStatusId', null);
            formik.setFieldValue('contractEventContractCommissionStatusId', null);
            formik.setFieldValue('sendSms', false);
            formik.setFieldValue('sendEmail', true);
            return;
        }

        formik.setFieldValue('originStatusType', 'contract_invoicing');
        formik.setFieldValue('destinationStatusType', null);
        formik.setFieldValue('destinationStatusMode', 'specific');
        formik.setFieldValue('destinationStatusId', null);

        if (!formik.values.scheduleMode) {
            formik.setFieldValue('scheduleMode', 'weekly');
            formik.setFieldValue('scheduleWeekday', 2);
        }

        formik.setFieldValue('contractEventContractStatusId', null);
        formik.setFieldValue('contractEventContractProductStatusId', null);
        formik.setFieldValue('contractEventContractInvoiceStatusId', null);
        formik.setFieldValue('contractEventContractIncidenceStatusId', null);
        formik.setFieldValue('contractEventContractCommissionStatusId', null);
        formik.setFieldValue('sendSms', false);
        formik.setFieldValue('sendEmail', true);
    }, [formik.values.event]);

    useEffect(() => {
        const mode = formik.values.scheduleMode;
        if (mode === 'daily') {
            formik.setFieldValue('scheduleWeekday', null);
            formik.setFieldValue('scheduleDay', null);
            formik.setFieldValue('scheduleMonth', null);
            formik.setFieldValue('scheduleCustomDays', null);
            return;
        }

        if (mode === 'weekly') {
            formik.setFieldValue('scheduleHour', null);
            formik.setFieldValue('scheduleDay', null);
            formik.setFieldValue('scheduleMonth', null);
            formik.setFieldValue('scheduleCustomDays', null);
            return;
        }

        if (mode === 'monthly') {
            formik.setFieldValue('scheduleHour', null);
            formik.setFieldValue('scheduleWeekday', null);
            formik.setFieldValue('scheduleMonth', null);
            formik.setFieldValue('scheduleCustomDays', null);
            return;
        }

        if (mode === 'yearly') {
            formik.setFieldValue('scheduleHour', null);
            formik.setFieldValue('scheduleWeekday', null);
            formik.setFieldValue('scheduleCustomDays', null);
            return;
        }

        if (mode === 'custom_days') {
            formik.setFieldValue('scheduleHour', null);
            formik.setFieldValue('scheduleWeekday', null);
            formik.setFieldValue('scheduleDay', null);
            formik.setFieldValue('scheduleMonth', null);
            return;
        }
    }, [formik.values.scheduleMode]);

    useEffect(() => {
        if (['contract.register', EVENT_CONTRACT_SIGNATURE_RESEND].includes(formik.values.event)) {
            return;
        }

        formik.setFieldValue('sendSms', false);
    }, [formik.values.event]);

    useEffect(() => {
        if (formik.values.sendEmail) {
            return;
        }

        formik.setFieldValue('emailTemplateIds', []);
        formik.setFieldValue('emailTemplateId', null);
        formik.setFieldValue('sendEmailConsultancyCopy', false);
        formik.setFieldValue('emailAttachmentDocumentTypeIds', []);
        setIsPreviewModalOpen(false);
    }, [formik.values.sendEmail]);

    useEffect(() => {
        if (['contracts', 'expirations'].includes(formik.values.entity)) {
            return;
        }

        formik.setFieldValue('sendEmailConsultancyCopy', false);
        formik.setFieldValue('emailAttachmentDocumentTypeIds', []);
    }, [formik.values.entity]);

    useEffect(() => {
        if (formik.values.emailTemplateIds.length > 0) {
            return;
        }

        setIsPreviewModalOpen(false);
    }, [formik.values.emailTemplateIds]);

    useEffect(() => {
        if (!isOpen || !formik.values.sendEmail) {
            setEmailTemplateOptions([]);
            setIsLoadingEmailTemplates(false);
            setHasLoadedEmailTemplates(false);
            return;
        }

        const useType = getEmailTemplateUseType(formik.values.entity);
        if (!useType) {
            setEmailTemplateOptions([]);
            setIsLoadingEmailTemplates(false);
            setHasLoadedEmailTemplates(false);
            return;
        }

        let isCancelled = false;

        const loadEmailTemplates = async () => {
            setIsLoadingEmailTemplates(true);
            setHasLoadedEmailTemplates(false);

            try {
                const response = await (await userService.listEmailTemplates({
                    limit: 200,
                    page: 1,
                    filter_filters: {
                        use_type: useType,
                        ...(formik.values.contractProductTypeId ? { product_type_id: formik.values.contractProductTypeId } : {}),
                        ...(emailTemplateOrganisingCorporationFilterId ? { organising_corporation_id: emailTemplateOrganisingCorporationFilterId } : {}),
                    },
                    filter_order: [
                        { field: 'name', order: 'asc' },
                    ],
                })).getResponseData();

                if (isCancelled) {
                    return;
                }

                if (!response?.success) {
                    handleErrors(response);
                    setEmailTemplateOptions([]);
                    return;
                }

                const options = (response?.data?.data || []).map((template: any) => {
                    const products = Array.isArray(template.products) ? template.products : [];
                    const productsLabel = products.length > 0
                        ? ` | Productos: ${products.map((product: any) => product?.name).filter(Boolean).join(', ')}`
                        : ' | Todos los productos';

                    return {
                        label: `${template.name}${Array.isArray(template.organisingCorporations) && template.organisingCorporations.length > 0
                            ? ` (${template.organisingCorporations.map((organisingCorporation: any) => organisingCorporation?.name).filter(Boolean).join(', ')})`
                            : ' (Sin organizadora)'}${productsLabel}`,
                        value: template.id,
                        name: template.name,
                        templateContent: template.templateContent || '',
                        products,
                        organisingCorporations: Array.isArray(template.organisingCorporations) ? template.organisingCorporations : [],
                    };
                });

                setEmailTemplateOptions(options);
            } catch (error: any) {
                if (!isCancelled) {
                    handleErrors(error);
                    setEmailTemplateOptions([]);
                }
            } finally {
                if (!isCancelled) {
                    setIsLoadingEmailTemplates(false);
                    setHasLoadedEmailTemplates(true);
                }
            }
        };

        loadEmailTemplates();

        return () => {
            isCancelled = true;
            setIsLoadingEmailTemplates(false);
        };
    }, [emailTemplateOrganisingCorporationFilterId, formik.values.contractProductTypeId, formik.values.entity, formik.values.sendEmail, handleErrors, isOpen, userService]);

    useEffect(() => {
        if (formik.values.event !== EVENT_INVOICE_UNPAID_WEEKLY_REMINDER) {
            return;
        }

        if (formik.values.emailTemplateIds.length > 0) {
            return;
        }

        const friendlyReminderTemplate = emailTemplateOptions.find(
            (template) => template.name?.trim().toLowerCase() === 'recordatorio amistoso'
        );

        if (friendlyReminderTemplate) {
            formik.setFieldValue('emailTemplateIds', [friendlyReminderTemplate.value]);
            formik.setFieldValue('emailTemplateId', friendlyReminderTemplate.value);
        }
    }, [emailTemplateOptions, formik.values.emailTemplateIds, formik.values.event]);

    useEffect(() => {
        if (!isOpen) {
            setEmailTemplateOrganisingCorporationFilterId(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!hasLoadedEmailTemplates) {
            return;
        }

        const validSelectedIds = formik.values.emailTemplateIds.filter((emailTemplateId) =>
            emailTemplateOptions.some((option) => option.value === emailTemplateId)
        );

        if (validSelectedIds.length !== formik.values.emailTemplateIds.length) {
            formik.setFieldValue('emailTemplateIds', validSelectedIds);
        }

        formik.setFieldValue('emailTemplateId', validSelectedIds[0] || null);
    }, [emailTemplateOptions, formik.values.emailTemplateIds, hasLoadedEmailTemplates]);

    const isContractEntity = formik.values.entity === 'contracts';
    const isOrderEntity = formik.values.entity === 'orders';
    const isContractChangeStatusEvent = formik.values.event === 'contract.change.status';
    const isContractDocumentUploadEvent = formik.values.event === EVENT_CONTRACT_DOCUMENT_UPLOAD;
    const isOrderChangeStatusEvent = formik.values.event === EVENT_ORDER_CHANGE_STATUS;
    const isContractCourseDateTodayEvent = CONTRACT_COURSE_DATE_EVENTS.includes(formik.values.event);
    const isContractSignatureResendEvent = formik.values.event === EVENT_CONTRACT_SIGNATURE_RESEND;
    const isExpirationManagementEvent = formik.values.event === 'expiration.management.change.status';
    const isExpirationRemittanceEvent = [EVENT_EXPIRATION_REMITTANCE_GENERATE, EVENT_EXPIRATION_REMITTANCE_CONFIRM].includes(formik.values.event);
    const isInvoiceUnpaidReminderEvent = formik.values.event === EVENT_INVOICE_UNPAID_WEEKLY_REMINDER;
    const isInvoiceUnpaidMonthlyExpirationsReminderEvent = formik.values.event === EVENT_INVOICE_UNPAID_MONTHLY_EXPIRATIONS_REMINDER;
    const isContractInStatusWithoutIncidenceAndPaidInvoiceEvent =
        formik.values.event === EVENT_CONTRACT_IN_STATUS_WITHOUT_INCIDENCE_AND_PAID_INVOICE;
    const isInvoiceAutomatedReminderEvent = isInvoiceUnpaidReminderEvent || isInvoiceUnpaidMonthlyExpirationsReminderEvent;
    const isForcedEmailEvent = isInvoiceAutomatedReminderEvent || isContractInStatusWithoutIncidenceAndPaidInvoiceEvent;
    const showsContractDestinationStatusesSection =
        (isContractEntity
            && !isContractChangeStatusEvent
            && !isInvoiceAutomatedReminderEvent
            && !isContractInStatusWithoutIncidenceAndPaidInvoiceEvent
            && !isContractSignatureResendEvent)
        || (isOrderEntity && isOrderChangeStatusEvent);
    const supportsEmailTemplates = Boolean(getEmailTemplateUseType(formik.values.entity));
    const supportsAttachmentDocumentTypes = ['contracts', 'expirations'].includes(formik.values.entity);
    const selectedEmailTemplateOption = useMemo(
        () => emailTemplateOptions.find((option) => option.value === formik.values.emailTemplateIds[0]) || null,
        [emailTemplateOptions, formik.values.emailTemplateIds]
    );
    const previewEmailHtml = useMemo(
        () => buildBrandedEmailPreviewTemplate(
            formik.values.name?.trim() || 'Comunicación Attiva',
            selectedEmailTemplateOption?.templateContent
        ),
        [formik.values.name, selectedEmailTemplateOption]
    );
    const currentEventOptions = getEventOptionsByEntity(formik.values.entity);
    const originOptions = toSelectOptions(stateOptionsByType[formik.values.originStatusType || ''] || []);
    const destinationOptions = toSelectOptions(stateOptionsByType[formik.values.destinationStatusType || ''] || []);

    return (
        <>
            <Modal isOpen={isOpen} onClose={handleClose} size="xl">
                <div className="m-auto px-7 pt-6 pb-8 max-h-[85vh] overflow-y-auto">
                    <div className="mb-7 flex items-center justify-between">
                        <Text className="text-lg font-semibold text-gray-900">
                            {mode} evento
                        </Text>
                        <ActionIcon
                            size="sm"
                            variant="text"
                            onClick={handleClose}
                            className="p-0 text-gray-500 hover:!text-gray-900"
                        >
                            <IoClose className="h-[18px] w-[18px]" />
                        </ActionIcon>
                    </div>

                <form onSubmit={formik.handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <RequiredInput
                            id='name'
                            label="Nombre interno"
                            formik={formik}
                            required={true}
                        />
                    </div>

                    <div className="md:col-span-2">
                        <Textarea
                            id="description"
                            name="description"
                            label="Descripción (opcional)"
                            rows={2}
                            value={formik.values.description || ''}
                            onChange={formik.handleChange}
                            error={formik.errors.description as string}
                        />
                    </div>

                    <Select
                        label="Entidad"
                        options={ENTITY_OPTIONS}
                        value={mapSelectOption(formik.values.entity, ENTITY_OPTIONS)}
                        onChange={(option: any) => {
                            formik.setFieldValue('entity', option?.value);
                            formik.setFieldValue('emailTemplateIds', []);
                            formik.setFieldValue('emailTemplateId', null);
                        }}
                        error={formik.errors.entity as string}
                    />

                    <Select
                        label="Evento"
                        options={currentEventOptions}
                        placeholder="Selecciona un evento"
                        value={mapSelectOption(formik.values.event, currentEventOptions)}
                        onChange={(option: any) => formik.setFieldValue('event', option?.value)}
                        error={formik.errors.event as string}
                    />

                    {isContractEntity && ['contract.register', EVENT_CONTRACT_SIGNATURE_RESEND].includes(formik.values.event) && (
                        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-3">
                            <Switch
                                id="sendSms"
                                label="Enviar SMS a cliente"
                                checked={formik.values.sendSms}
                                onChange={formik.handleChange}
                            />
                        </div>
                    )}

                    {supportsEmailTemplates && (
                        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-3">
                            <Switch
                                id="sendEmail"
                                label="Enviar email"
                                checked={isForcedEmailEvent ? true : formik.values.sendEmail}
                                onChange={isForcedEmailEvent ? undefined : formik.handleChange}
                                disabled={isForcedEmailEvent}
                            />
                        </div>
                    )}

                    {supportsEmailTemplates && formik.values.sendEmail && (
                        <div className="md:col-span-2">
                            <Select
                                label="Filtrar plantillas por organizadora"
                                placeholder="todas"
                                options={getOrganisingCorporationsList()}
                                value={emailTemplateOrganisingCorporationFilterId
                                    ? getOrganisingCorporationsList().find((option: any) => option.value === emailTemplateOrganisingCorporationFilterId) || null
                                    : null}
                                onChange={(option: any) => setEmailTemplateOrganisingCorporationFilterId(option?.value || null)}
                                clearable
                            />

                            <CustomSelect
                                id="emailTemplateIds"
                                label="Plantilla de email"
                                placeholder={isLoadingEmailTemplates ? 'Cargando plantillas...' : 'Selecciona una plantilla'}
                                options={emailTemplateOptions}
                                value={mapMultiSelectOptions(formik.values.emailTemplateIds, emailTemplateOptions)}
                                onChange={(options: any) => {
                                    const ids = Array.isArray(options) ? options.map((option: any) => option.value) : [];
                                    formik.setFieldValue('emailTemplateIds', ids);
                                    formik.setFieldValue('emailTemplateId', ids[0] || null);
                                }}
                                isMulti
                                isSearchable
                                isClearable
                                error={formik.errors.emailTemplateIds as string}
                            />
                            <div className="mt-2 flex justify-end">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setIsPreviewModalOpen(true)}
                                    disabled={formik.values.emailTemplateIds.length === 0}
                                >
                                    Previsualizar email
                                </Button>
                            </div>
                            {!isLoadingEmailTemplates && emailTemplateOptions.length === 0 && (
                                <Text className="mt-1 text-xs text-gray-500">
                                    No hay plantillas disponibles para la entidad seleccionada.
                                </Text>
                            )}
                            {isInvoiceUnpaidReminderEvent && (
                                <Text className="mt-1 text-xs text-gray-500">
                                    Para este evento se recomienda la plantilla "Recordatorio Amistoso" (se preselecciona automáticamente si existe).
                                </Text>
                            )}
                            {isInvoiceUnpaidMonthlyExpirationsReminderEvent && (
                                <Text className="mt-1 text-xs text-gray-500">
                                    Este evento envía email el día del mes configurado a clientes con vencimientos impagados del mes actual.
                                </Text>
                            )}
                            {isContractInStatusWithoutIncidenceAndPaidInvoiceEvent && (
                                <Text className="mt-1 text-xs text-gray-500">
                                    Este evento requiere email y enviará los documentos adjuntos por tipo al cumplirse el número de días configurado.
                                </Text>
                            )}

                            {supportsAttachmentDocumentTypes && (
                                <>
                                    <div className="mt-3 rounded-lg border border-dashed border-gray-300 bg-white p-3">
                                        <Switch
                                            id="sendEmailConsultancyCopy"
                                            label="Incluir gestoría del cliente en copia (CC)"
                                            checked={formik.values.sendEmailConsultancyCopy}
                                            onChange={formik.handleChange}
                                        />
                                        <Text className="mt-1 text-xs text-gray-500">
                                            Si el contrato no tiene gestoría o email de gestoría, no se añadirá copia.
                                        </Text>
                                    </div>

                                    <div className="mt-3">
                                        <CustomSelect
                                            id="emailAttachmentDocumentTypeIds"
                                            label="Tipos de documento a adjuntar"
                                            isMulti
                                            isSearchable
                                            isClearable
                                            options={contractDocumentTypeOptions}
                                            value={mapMultiSelectOptions(
                                                formik.values.emailAttachmentDocumentTypeIds,
                                                contractDocumentTypeOptions
                                            )}
                                            onChange={(selectedOptions: any) => {
                                                const selectedIds = Array.isArray(selectedOptions)
                                                    ? selectedOptions.map((option) => Number(option.value))
                                                    : [];
                                                formik.setFieldValue('emailAttachmentDocumentTypeIds', selectedIds);
                                            }}
                                            error={formik.errors.emailAttachmentDocumentTypeIds as string}
                                            placeholder="uno o varios tipos"
                                        />
                                        <Text className="mt-1 text-xs text-gray-500">
                                            Se adjuntarán todos los documentos del contrato que pertenezcan a los tipos seleccionados.
                                        </Text>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {(isContractEntity || isOrderEntity) && (
                        <Select
                            label="Tipo de producto (opcional)"
                            placeholder="Aplicar a todos los tipos"
                            options={getProductTypesList()}
                            value={mapSelectOption(formik.values.contractProductTypeId, getProductTypesList())}
                            onChange={(option: any) => formik.setFieldValue('contractProductTypeId', option?.value || null)}
                            error={formik.errors.contractProductTypeId as string}
                        />
                    )}

                    {isContractEntity && isContractDocumentUploadEvent && (
                        <CustomSelect
                            id="contractDocumentTypeIds"
                            label="Tipos de documento disparador"
                            isMulti
                            isSearchable
                            isClearable
                            options={contractDocumentTypeOptions}
                            value={mapMultiSelectOptions(formik.values.contractDocumentTypeIds, contractDocumentTypeOptions)}
                            onChange={(selectedOptions: any) => {
                                const selectedIds = Array.isArray(selectedOptions)
                                    ? selectedOptions.map((option: any) => Number(option.value))
                                    : [];
                                formik.setFieldValue('contractDocumentTypeIds', selectedIds);
                            }}
                            error={formik.errors.contractDocumentTypeIds as string}
                            placeholder="uno o varios tipos"
                        />
                    )}

                    {isContractEntity && isContractInStatusWithoutIncidenceAndPaidInvoiceEvent && (
                        <div className="md:col-span-2 rounded-xl border border-dashed border-gray-300 p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50">
                            <Text className="md:col-span-2 text-xs uppercase tracking-wide text-gray-500">
                                Contrato en estado durante X días
                            </Text>

                            <Select
                                label="Estado de contrato monitorizado"
                                options={toSelectOptions(contractStateOptions)}
                                value={mapSelectOption(formik.values.originStatusId, toSelectOptions(contractStateOptions))}
                                onChange={(option: any) => formik.setFieldValue('originStatusId', option?.value || null)}
                                error={formik.errors.originStatusId as string}
                                placeholder={"Selecciona un estado"}
                            />

                            <Select
                                label="Estado de producto destino"
                                options={toSelectOptions(contractProductStateOptions)}
                                value={mapSelectOption(formik.values.contractEventContractProductStatusId, toSelectOptions(contractProductStateOptions))}
                                onChange={(option: any) => formik.setFieldValue('contractEventContractProductStatusId', option?.value || null)}
                                error={formik.errors.contractEventContractProductStatusId as string}
                                placeholder={"Selecciona un estado"}
                            />

                            <Input
                                label="Número de días en estado"
                                type="number"
                                min={1}
                                value={formik.values.scheduleCustomDays ?? ''}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    formik.setFieldValue('scheduleCustomDays', value ? Number(value) : null);
                                }}
                                error={formik.errors.scheduleCustomDays as string}
                            />

                            <div className="rounded-lg border border-dashed border-blue-300 bg-blue-50 px-3 py-2">
                                <Text className="text-[11px] uppercase tracking-wide text-blue-700">
                                    Condiciones automáticas adicionales
                                </Text>
                                <Text className="text-sm font-semibold text-blue-900">
                                    Sin incidencia activa (o incidencia satisfactoria) y facturación satisfactoria.
                                </Text>
                            </div>
                        </div>
                    )}

                    {isContractEntity && isContractSignatureResendEvent && (
                        <div className="md:col-span-2 rounded-xl border border-dashed border-gray-300 p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50">
                            <Text className="md:col-span-2 text-xs uppercase tracking-wide text-gray-500">
                                Reenvío de firma de contrato
                            </Text>

                            <Input
                                label="Minutos mínimos entre reenvíos"
                                type="number"
                                min={1}
                                value={formik.values.scheduleCustomDays ?? ''}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    formik.setFieldValue('scheduleCustomDays', value ? Number(value) : null);
                                }}
                                error={formik.errors.scheduleCustomDays as string}
                            />

                            <div className="rounded-lg border border-dashed border-blue-300 bg-blue-50 px-3 py-2">
                                <Text className="text-[11px] uppercase tracking-wide text-blue-700">
                                    Configuración
                                </Text>
                                <Text className="text-sm font-semibold text-blue-900">
                                    Define el tiempo mínimo entre envíos y los canales (SMS y/o email con plantilla).
                                </Text>
                            </div>
                        </div>
                    )}

                    {isOrderEntity && isOrderChangeStatusEvent && (
                        <Select
                            label="Estado de pedido disparador"
                            options={toSelectOptions(getOrderStatusesList())}
                            value={mapSelectOption(formik.values.originStatusId, toSelectOptions(getOrderStatusesList()))}
                            onChange={(option: any) => formik.setFieldValue('originStatusId', option?.value || null)}
                            error={formik.errors.originStatusId as string}
                            placeholder={"Selecciona el estado de pedido"}
                        />
                    )}

                    {showsContractDestinationStatusesSection && (
                        <div className="md:col-span-2 rounded-xl border border-dashed border-gray-300 p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50">
                            <Text className="md:col-span-2 text-xs uppercase tracking-wide text-gray-500">
                                Estados destino
                            </Text>

                            <Select
                                label="Estado de contrato"
                                options={toSelectOptions(contractStateOptions)}
                                value={mapSelectOption(formik.values.contractEventContractStatusId, toSelectOptions(contractStateOptions))}
                                onChange={(option: any) => formik.setFieldValue('contractEventContractStatusId', option?.value || null)}
                                clearable
                                onClear={() => formik.setFieldValue('contractEventContractStatusId', null)}
                                error={formik.errors.contractEventContractStatusId as string}
                                placeholder={"Selecciona un estado"}
                            />

                            <Select
                                label="Estado de producto"
                                options={toSelectOptions(contractProductStateOptions)}
                                value={mapSelectOption(formik.values.contractEventContractProductStatusId, toSelectOptions(contractProductStateOptions))}
                                onChange={(option: any) => formik.setFieldValue('contractEventContractProductStatusId', option?.value || null)}
                                clearable
                                onClear={() => formik.setFieldValue('contractEventContractProductStatusId', null)}
                                error={formik.errors.contractEventContractProductStatusId as string}
                                placeholder={"Selecciona un estado"}
                            />

                            <Select
                                label="Estado de facturación"
                                options={toSelectOptions(contractInvoiceStateOptions)}
                                value={mapSelectOption(formik.values.contractEventContractInvoiceStatusId, toSelectOptions(contractInvoiceStateOptions))}
                                onChange={(option: any) => formik.setFieldValue('contractEventContractInvoiceStatusId', option?.value || null)}
                                clearable
                                onClear={() => formik.setFieldValue('contractEventContractInvoiceStatusId', null)}
                                error={formik.errors.contractEventContractInvoiceStatusId as string}
                                placeholder={"Selecciona un estado"}
                            />

                            <Select
                                label="Estado de incidencia"
                                options={toSelectOptions(contractIncidenceStateOptions)}
                                value={mapSelectOption(formik.values.contractEventContractIncidenceStatusId, toSelectOptions(contractIncidenceStateOptions))}
                                onChange={(option: any) => formik.setFieldValue('contractEventContractIncidenceStatusId', option?.value || null)}
                                clearable
                                onClear={() => formik.setFieldValue('contractEventContractIncidenceStatusId', null)}
                                error={formik.errors.contractEventContractIncidenceStatusId as string}
                                placeholder={"Selecciona un estado"}
                            />

                            <Select
                                label="Estado de comisión"
                                options={toSelectOptions(contractCommissionStateOptions)}
                                value={mapSelectOption(formik.values.contractEventContractCommissionStatusId, toSelectOptions(contractCommissionStateOptions))}
                                onChange={(option: any) => formik.setFieldValue('contractEventContractCommissionStatusId', option?.value || null)}
                                clearable
                                onClear={() => formik.setFieldValue('contractEventContractCommissionStatusId', null)}
                                error={formik.errors.contractEventContractCommissionStatusId as string}
                                placeholder={"Selecciona un estado"}
                            />

                            {['contract.sign', 'contract.register', EVENT_CONTRACT_CLIENT_UPDATE_CREDITS, EVENT_CONTRACT_DOCUMENT_UPLOAD, EVENT_CONTRACT_REMITTANCE_CONFIRM, EVENT_ORDER_CHANGE_STATUS, ...CONTRACT_COURSE_DATE_EVENTS].includes(formik.values.event) && (
                                <Text className="md:col-span-2 text-xs text-gray-500">
                                    Debes seleccionar al menos uno de estos 5 estados destino.
                                </Text>
                            )}
                        </div>
                    )}

                    {isContractChangeStatusEvent && (
                        <div className="md:col-span-2 rounded-xl border border-dashed border-gray-300 p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50">
                            <Text className="md:col-span-2 text-xs uppercase tracking-wide text-gray-500">
                                Cambio de estado
                            </Text>

                            <Text className="md:col-span-2 text-xs uppercase tracking-wide text-gray-500">
                                Estado de origen
                            </Text>

                            <Select
                                label="Tipo de estado origen"
                                options={CONTRACT_STATUS_TYPE_OPTIONS}
                                value={mapSelectOption(formik.values.originStatusType, CONTRACT_STATUS_TYPE_OPTIONS)}
                                onChange={(option: any) => {
                                    formik.setFieldValue('originStatusType', option?.value || null);
                                    formik.setFieldValue('originStatusId', null);
                                }}
                                error={formik.errors.originStatusType as string}
                                placeholder={"Selecciona un tipo de estado (entidad)"}
                            />

                            <Select
                                label="Estado origen"
                                options={originOptions}
                                value={mapSelectOption(formik.values.originStatusId, originOptions)}
                                onChange={(option: any) => formik.setFieldValue('originStatusId', option?.value || null)}
                                error={formik.errors.originStatusId as string}
                                placeholder={"Selecciona un estado"}
                            />

                            <Text className="md:col-span-2 text-xs uppercase tracking-wide text-gray-500">
                                Estado destino
                            </Text>

                            <Select
                                label="Modo de estado destino"
                                options={DESTINATION_STATUS_MODE_OPTIONS}
                                value={mapSelectOption(formik.values.destinationStatusMode, DESTINATION_STATUS_MODE_OPTIONS)}
                                onChange={(option: any) => formik.setFieldValue('destinationStatusMode', option?.value || 'specific')}
                                error={formik.errors.destinationStatusMode as string}
                                placeholder={"Selecciona un modo"}
                            />

                            <Select
                                label="Tipo de estado destino"
                                options={CONTRACT_STATUS_TYPE_OPTIONS}
                                value={mapSelectOption(formik.values.destinationStatusType, CONTRACT_STATUS_TYPE_OPTIONS)}
                                onChange={(option: any) => {
                                    formik.setFieldValue('destinationStatusType', option?.value || null);
                                    formik.setFieldValue('destinationStatusId', null);
                                }}
                                error={formik.errors.destinationStatusType as string}
                                placeholder={"Selecciona un tipo de estado (entidad)"}
                            />

                            {formik.values.destinationStatusMode === 'specific' ? (
                                <Select
                                    label="Estado destino"
                                    options={destinationOptions}
                                    value={mapSelectOption(formik.values.destinationStatusId, destinationOptions)}
                                    onChange={(option: any) => formik.setFieldValue('destinationStatusId', option?.value || null)}
                                    error={formik.errors.destinationStatusId as string}
                                    placeholder={"Selecciona un estado"}
                                />
                            ) : (
                                <div className="rounded-lg border border-dashed border-blue-300 bg-blue-50 px-3 py-2">
                                    <Text className="text-xs uppercase tracking-wide text-blue-700">
                                        Estado destino
                                    </Text>
                                    <Text className="mt-1 text-sm font-semibold text-blue-900">
                                        Estado anterior (si existe)
                                    </Text>
                                </div>
                            )}
                        </div>
                    )}

                    {isExpirationManagementEvent && (
                        <div className="md:col-span-2 rounded-xl border border-dashed border-gray-300 p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50">
                            <Text className="md:col-span-2 text-xs uppercase tracking-wide text-gray-500">
                                Cambio de estado de gestión de cobro
                            </Text>

                            <Select
                                label="Estado de origen"
                                options={toSelectOptions(getExpirationManagementStatesList())}
                                value={mapSelectOption(formik.values.originStatusId, toSelectOptions(getExpirationManagementStatesList()))}
                                onChange={(option: any) => formik.setFieldValue('originStatusId', option?.value || null)}
                                error={formik.errors.originStatusId as string}
                                placeholder={"Selecciona un estado"}
                            />

                            <Select
                                label="Estado de destino"
                                options={toSelectOptions(getExpirationManagementStatesList())}
                                value={mapSelectOption(formik.values.destinationStatusId, toSelectOptions(getExpirationManagementStatesList()))}
                                onChange={(option: any) => formik.setFieldValue('destinationStatusId', option?.value || null)}
                                error={formik.errors.destinationStatusId as string}
                                placeholder={"Selecciona un estado"}
                            />

                            <div className="md:col-span-2 rounded-lg border border-dashed border-gray-300 bg-white p-3">
                                <Text className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                                    Programación temporal
                                </Text>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Select
                                        label="Frecuencia"
                                        options={TEMPORAL_SCHEDULE_MODE_OPTIONS}
                                        value={mapSelectOption(formik.values.scheduleMode, TEMPORAL_SCHEDULE_MODE_OPTIONS)}
                                        onChange={(option: any) => formik.setFieldValue('scheduleMode', option?.value || null)}
                                        error={formik.errors.scheduleMode as string}
                                        placeholder={"Selecciona una frecuencia"}
                                    />

                                    {formik.values.scheduleMode === 'daily' && (
                                        <Input
                                            label="Hora"
                                            type="time"
                                            value={formik.values.scheduleHour || ''}
                                            onChange={(event) => formik.setFieldValue('scheduleHour', event.target.value || null)}
                                            error={formik.errors.scheduleHour as string}
                                        />
                                    )}

                                    {formik.values.scheduleMode === 'weekly' && (
                                        <Select
                                            label="Día de la semana"
                                            options={WEEKDAY_OPTIONS}
                                            value={mapSelectOption(formik.values.scheduleWeekday, WEEKDAY_OPTIONS)}
                                            onChange={(option: any) => formik.setFieldValue('scheduleWeekday', option?.value ?? null)}
                                            error={formik.errors.scheduleWeekday as string}
                                            placeholder={"Selecciona un día"}
                                        />
                                    )}

                                    {formik.values.scheduleMode === 'monthly' && (
                                        <Select
                                            label="Día del mes"
                                            options={DAY_OF_MONTH_OPTIONS}
                                            value={mapSelectOption(formik.values.scheduleDay, DAY_OF_MONTH_OPTIONS)}
                                            onChange={(option: any) => formik.setFieldValue('scheduleDay', option?.value ?? null)}
                                            error={formik.errors.scheduleDay as string}
                                            placeholder={"Selecciona un día"}
                                        />
                                    )}

                                    {formik.values.scheduleMode === 'yearly' && (
                                        <>
                                            <Select
                                                label="Día del mes"
                                                options={DAY_OF_MONTH_OPTIONS}
                                                value={mapSelectOption(formik.values.scheduleDay, DAY_OF_MONTH_OPTIONS)}
                                                onChange={(option: any) => formik.setFieldValue('scheduleDay', option?.value ?? null)}
                                                error={formik.errors.scheduleDay as string}
                                                placeholder={"Selecciona un día"}
                                            />

                                            <Select
                                                label="Mes"
                                                options={MONTH_OPTIONS}
                                                value={mapSelectOption(formik.values.scheduleMonth, MONTH_OPTIONS)}
                                                onChange={(option: any) => formik.setFieldValue('scheduleMonth', option?.value ?? null)}
                                                error={formik.errors.scheduleMonth as string}
                                                placeholder={"Selecciona un mes"}
                                            />
                                        </>
                                    )}

                                    {formik.values.scheduleMode === 'custom_days' && (
                                        <Input
                                            label="Número de días"
                                            type="number"
                                            min={1}
                                            value={formik.values.scheduleCustomDays ?? ''}
                                            onChange={(event) => {
                                                const value = event.target.value;
                                                formik.setFieldValue('scheduleCustomDays', value ? Number(value) : null);
                                            }}
                                            error={formik.errors.scheduleCustomDays as string}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {formik.values.entity === 'expirations' && isExpirationRemittanceEvent && (
                        <div className="md:col-span-2 rounded-xl border border-dashed border-gray-300 p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50">
                            <Text className="md:col-span-2 text-xs uppercase tracking-wide text-gray-500">
                                {formik.values.event === EVENT_EXPIRATION_REMITTANCE_GENERATE
                                    ? 'Cambio de estado al generar remesa'
                                    : 'Cambio de estado al confirmar remesa'}
                            </Text>

                            <Select
                                label="Estado de vencimiento destino"
                                options={toSelectOptions(getExpirationStatesList())}
                                value={mapSelectOption(formik.values.destinationStatusId, toSelectOptions(getExpirationStatesList()))}
                                onChange={(option: any) => formik.setFieldValue('destinationStatusId', option?.value || null)}
                                error={formik.errors.destinationStatusId as string}
                                placeholder={"Selecciona un estado"}
                            />

                            <div className="rounded-lg border border-dashed border-blue-300 bg-blue-50 px-3 py-2">
                                <Text className="text-[11px] uppercase tracking-wide text-blue-700">
                                    Disparador
                                </Text>
                                <Text className="text-sm font-semibold text-blue-900">
                                    {formik.values.event === EVENT_EXPIRATION_REMITTANCE_GENERATE
                                        ? 'Generación de remesa'
                                        : 'Confirmación de remesa'}
                                </Text>
                            </div>
                        </div>
                    )}

                    {isContractEntity && isContractCourseDateTodayEvent && (
                        <div className="md:col-span-2 rounded-xl border border-dashed border-gray-300 p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50">
                            <Text className="md:col-span-2 text-xs uppercase tracking-wide text-gray-500">
                                {formik.values.event === EVENT_CONTRACT_COURSE_END_TODAY
                                    ? 'Frecuencia de comprobación (fin de curso hoy)'
                                    : 'Frecuencia de comprobación (inicio de curso hoy)'}
                            </Text>

                            <Select
                                label="Estado de producto previo requerido"
                                options={toSelectOptions(contractProductStateOptions)}
                                value={mapSelectOption(formik.values.originStatusId, toSelectOptions(contractProductStateOptions))}
                                onChange={(option: any) => formik.setFieldValue('originStatusId', option?.value || null)}
                                error={formik.errors.originStatusId as string}
                                placeholder={"Selecciona el estado de producto"}
                            />

                            <Select
                                label="Frecuencia"
                                options={COURSE_START_TODAY_SCHEDULE_MODE_OPTIONS}
                                value={mapSelectOption(formik.values.scheduleMode, COURSE_START_TODAY_SCHEDULE_MODE_OPTIONS)}
                                onChange={(option: any) => formik.setFieldValue('scheduleMode', option?.value || null)}
                                error={formik.errors.scheduleMode as string}
                                placeholder={"Selecciona una frecuencia"}
                            />

                            {formik.values.scheduleMode === 'daily' && (
                                <Input
                                    label="Hora"
                                    type="time"
                                    value={formik.values.scheduleHour || ''}
                                    onChange={(event) => formik.setFieldValue('scheduleHour', event.target.value || null)}
                                    error={formik.errors.scheduleHour as string}
                                />
                            )}

                            {formik.values.scheduleMode === 'weekly' && (
                                <Select
                                    label="Día de la semana"
                                    options={WEEKDAY_OPTIONS}
                                    value={mapSelectOption(formik.values.scheduleWeekday, WEEKDAY_OPTIONS)}
                                    onChange={(option: any) => formik.setFieldValue('scheduleWeekday', option?.value ?? null)}
                                    error={formik.errors.scheduleWeekday as string}
                                    placeholder={"Selecciona un día"}
                                />
                            )}

                            {formik.values.scheduleMode === 'monthly' && (
                                <Select
                                    label="Día del mes"
                                    options={DAY_OF_MONTH_OPTIONS}
                                    value={mapSelectOption(formik.values.scheduleDay, DAY_OF_MONTH_OPTIONS)}
                                    onChange={(option: any) => formik.setFieldValue('scheduleDay', option?.value ?? null)}
                                    error={formik.errors.scheduleDay as string}
                                    placeholder={"Selecciona un día"}
                                />
                            )}

                            {formik.values.scheduleMode === 'yearly' && (
                                <>
                                    <Select
                                        label="Día del mes"
                                        options={DAY_OF_MONTH_OPTIONS}
                                        value={mapSelectOption(formik.values.scheduleDay, DAY_OF_MONTH_OPTIONS)}
                                        onChange={(option: any) => formik.setFieldValue('scheduleDay', option?.value ?? null)}
                                        error={formik.errors.scheduleDay as string}
                                        placeholder={"Selecciona un día"}
                                    />

                                    <Select
                                        label="Mes"
                                        options={MONTH_OPTIONS}
                                        value={mapSelectOption(formik.values.scheduleMonth, MONTH_OPTIONS)}
                                        onChange={(option: any) => formik.setFieldValue('scheduleMonth', option?.value ?? null)}
                                        error={formik.errors.scheduleMonth as string}
                                        placeholder={"Selecciona un mes"}
                                    />
                                </>
                            )}
                        </div>
                    )}

                    {isContractEntity && isInvoiceUnpaidReminderEvent && (
                        <div className="md:col-span-2 rounded-xl border border-dashed border-gray-300 p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50">
                            <Text className="md:col-span-2 text-xs uppercase tracking-wide text-gray-500">
                                Recordatorio de factura impagada
                            </Text>

                            <Select
                                label="Estado de facturación del contrato"
                                options={toSelectOptions(contractInvoiceStateOptions)}
                                value={mapSelectOption(formik.values.originStatusId, toSelectOptions(contractInvoiceStateOptions))}
                                onChange={(option: any) => formik.setFieldValue('originStatusId', option?.value || null)}
                                error={formik.errors.originStatusId as string}
                                placeholder={"Selecciona el estado a monitorizar"}
                            />

                            <div className="md:col-span-2 rounded-lg border border-dashed border-gray-300 bg-white p-3">
                                <Text className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                                    Programación temporal
                                </Text>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Select
                                        label="Frecuencia"
                                        options={TEMPORAL_SCHEDULE_MODE_OPTIONS}
                                        value={mapSelectOption(formik.values.scheduleMode, TEMPORAL_SCHEDULE_MODE_OPTIONS)}
                                        onChange={(option: any) => formik.setFieldValue('scheduleMode', option?.value || null)}
                                        error={formik.errors.scheduleMode as string}
                                        placeholder={"Selecciona una frecuencia"}
                                    />

                                    {formik.values.scheduleMode === 'daily' && (
                                        <Input
                                            label="Hora"
                                            type="time"
                                            value={formik.values.scheduleHour || ''}
                                            onChange={(event) => formik.setFieldValue('scheduleHour', event.target.value || null)}
                                            error={formik.errors.scheduleHour as string}
                                        />
                                    )}

                                    {formik.values.scheduleMode === 'weekly' && (
                                        <Select
                                            label="Día de la semana"
                                            options={WEEKDAY_OPTIONS}
                                            value={mapSelectOption(formik.values.scheduleWeekday, WEEKDAY_OPTIONS)}
                                            onChange={(option: any) => formik.setFieldValue('scheduleWeekday', option?.value ?? null)}
                                            error={formik.errors.scheduleWeekday as string}
                                            placeholder={"Selecciona un día"}
                                        />
                                    )}

                                    {formik.values.scheduleMode === 'monthly' && (
                                        <Select
                                            label="Día del mes"
                                            options={DAY_OF_MONTH_OPTIONS}
                                            value={mapSelectOption(formik.values.scheduleDay, DAY_OF_MONTH_OPTIONS)}
                                            onChange={(option: any) => formik.setFieldValue('scheduleDay', option?.value ?? null)}
                                            error={formik.errors.scheduleDay as string}
                                            placeholder={"Selecciona un día"}
                                        />
                                    )}

                                    {formik.values.scheduleMode === 'yearly' && (
                                        <>
                                            <Select
                                                label="Día del mes"
                                                options={DAY_OF_MONTH_OPTIONS}
                                                value={mapSelectOption(formik.values.scheduleDay, DAY_OF_MONTH_OPTIONS)}
                                                onChange={(option: any) => formik.setFieldValue('scheduleDay', option?.value ?? null)}
                                                error={formik.errors.scheduleDay as string}
                                                placeholder={"Selecciona un día"}
                                            />

                                            <Select
                                                label="Mes"
                                                options={MONTH_OPTIONS}
                                                value={mapSelectOption(formik.values.scheduleMonth, MONTH_OPTIONS)}
                                                onChange={(option: any) => formik.setFieldValue('scheduleMonth', option?.value ?? null)}
                                                error={formik.errors.scheduleMonth as string}
                                                placeholder={"Selecciona un mes"}
                                            />
                                        </>
                                    )}

                                    {formik.values.scheduleMode === 'custom_days' && (
                                        <Input
                                            label="Número de días"
                                            type="number"
                                            min={1}
                                            value={formik.values.scheduleCustomDays ?? ''}
                                            onChange={(event) => {
                                                const value = event.target.value;
                                                formik.setFieldValue('scheduleCustomDays', value ? Number(value) : null);
                                            }}
                                            error={formik.errors.scheduleCustomDays as string}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {isContractEntity && isInvoiceUnpaidMonthlyExpirationsReminderEvent && (
                        <div className="md:col-span-2 rounded-xl border border-dashed border-gray-300 p-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50">
                            <Text className="md:col-span-2 text-xs uppercase tracking-wide text-gray-500">
                                Recordatorio mensual de vencimientos próximo a cobro
                            </Text>

                            <Select
                                label="Día del mes de envío"
                                options={DAY_OF_MONTH_OPTIONS}
                                value={mapSelectOption(formik.values.scheduleDay, DAY_OF_MONTH_OPTIONS)}
                                onChange={(option: any) => formik.setFieldValue('scheduleDay', option?.value ?? null)}
                                error={formik.errors.scheduleDay as string}
                                placeholder={"Selecciona un día"}
                            />

                            <div className="rounded-lg border border-dashed border-blue-300 bg-blue-50 px-3 py-2">
                                <Text className="text-[11px] uppercase tracking-wide text-blue-700">
                                    Alcance
                                </Text>
                                <Text className="text-sm font-semibold text-blue-900">
                                    Contratos con vencimientos impagados de factura cliente del mes actual.
                                </Text>
                            </div>
                        </div>
                    )}

                    <div className="md:col-span-2 flex items-center justify-end gap-4 pt-4">
                        <Button
                            variant="outline"
                            onClick={handleClose}
                            className="w-full md:w-auto"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            isLoading={formik.isSubmitting}
                            disabled={!canSubmitEventForm}
                            className="w-full md:w-auto"
                        >
                            {mode}
                        </Button>
                    </div>
                </form>
                </div>
            </Modal>

            <Modal
                isOpen={isPreviewModalOpen}
                onClose={() => setIsPreviewModalOpen(false)}
                size="xl"
            >
                <div className="m-auto px-5 py-5">
                    <div className="mb-4 flex items-center justify-between">
                        <Text className="text-lg font-semibold text-gray-900">
                            Previsualización del email
                        </Text>
                        <div className="flex items-center gap-2">
                            {formik.values.emailTemplateId && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        const target = `${menuRoutes.settings.path}${menuRoutes.settings.templates}?editEmailTemplateId=${formik.values.emailTemplateId}`;
                                        setIsPreviewModalOpen(false);
                                        onClose();
                                        navigate(target);
                                    }}
                                >
                                    Editar plantilla
                                </Button>
                            )}
                            <ActionIcon
                                size="sm"
                                variant="text"
                                onClick={() => setIsPreviewModalOpen(false)}
                                className="p-0 text-gray-500 hover:!text-gray-900"
                            >
                                <IoClose className="h-[18px] w-[18px]" />
                            </ActionIcon>
                        </div>
                    </div>

                    <iframe
                        title="Previsualización email evento"
                        sandbox=""
                        srcDoc={previewEmailHtml}
                        className="h-[70vh] w-full rounded-lg border border-gray-200 bg-white"
                    />
                </div>
            </Modal>
        </>
    );
};

export default EventModalForm;
